import msToast from '@/components/msToast/index.js';
import { Buffer } from 'buffer';
import i18n, { loadedLocales } from '@/i18n/i18n';
import { MSEnum } from '@/common/enumeration';
import Vue from 'vue';
import { setTimeout } from 'timers';
import { Decimal } from 'decimal.js';
import apiConfig from '@/api/apiConfig';
import httpClient from '@/api/httpClient';
import store from '@/store/store';
import router from '@/router';
import EventBusGlobal, { GlobalEventName } from '@/sys/eventBusGlobal';
import { isNullOrEmpty } from '@/common/objectUtil';
import { msFormat } from '@/filters/filters';
import messageBox from '@/common/messageBox';
import messageBoxV2 from '@/common/messageBoxV2';
import moment from 'moment';
//Import thủ công locale tiếng Việt — các locale khác đã bị IgnorePlugin loại khỏi bundle (xem vue.config.js)
import 'moment/locale/vi';
import clone from 'lodash/clone';
import cloneDeep from 'lodash/cloneDeep';
import DateRange from '@/components/msdaterange/DateRange.js';
import { uuidv7 } from 'uuidv7';

moment.locale('vi');

//Key lưu giá trị điều kiện sort trong localstorage
const KeySortInLocalStorage = 'KeySortScreen';
const KeyLinkWebSite = 'ConnectInfo';
const KeyNotificationsInbot = 'NotificationsInbot';
const KeyCulture = 'culture';
//Key localstorage Meinvoice lưu trữ ngôn ngữ
const KeyLanguageMeinvoice = 'language';
const KeyTakePage = 'TakePage';

/**
 * Class chứa các function dùng chung cho hệ thống phía client
 * @author DNThang - 03.07.2019
 */
class CommonFunction {
	/**
	 * Lấy version vòng vặp hiện tại từ window.__env.ConfigApplication.appVersion
	 * Ví dụ: "R66.0.1" => trả về "R66"
	 * @returns {String} Version vòng vặp (vd: "R66")
	 * @author ttanh - 29.10.2025
	 */
	getCurrentRoundVersion() {
		try {
			// Kiểm tra xem có tồn tại window.__env.ConfigApplication.appVersion không
			if (!window.__env || !window.__env.ConfigApplication || !window.__env.ConfigApplication.appVersion) {
				return '';
			}

			const appVersion = window.__env.ConfigApplication.appVersion;

			// Tách chuỗi version theo dấu "." và lấy phần đầu tiên
			// Ví dụ: "R66.0.1" => ["R66", "0", "1"] => "R66"
			const versionParts = appVersion.split('.');
			if (versionParts.length > 0) {
				const version = versionParts[0];
				//Kiểm tra xem version có chữ R hay không
				// Trả về "R66"
				if (!version.includes('R')) return `R${version}`;
				return version;
			}

			return '';
		} catch (error) {
			console.error('Error getting current round version:', error);
			return '';
		}
	}

	/**
	 * Sniff MIME type từ magic bytes của ArrayBuffer.
	 * PDF: bắt đầu bằng %PDF (0x25 0x50 0x44 0x46)
	 * PNG: bắt đầu bởi 0x89 0x50 0x4E 0x47
	 * @param {ArrayBuffer} buffer
	 * @returns {String} - MIME type, mặc định 'application/octet-stream'
	 */
	sniffArrayBufferType(buffer) {
		if (!(buffer instanceof ArrayBuffer) || buffer.byteLength < 4) return 'application/octet-stream';
		const view = new Uint8Array(buffer, 0, 4);
		//PDF: 0x25 0x50 0x44 0x46 ("%PDF")
		if (view[0] === 0x25 && view[1] === 0x50 && view[2] === 0x44 && view[3] === 0x46) {
			return 'application/pdf';
		}
		//PNG: 0x89 0x50 0x4E 0x47
		if (view[0] === 0x89 && view[1] === 0x50 && view[2] === 0x4e && view[3] === 0x47) {
			return 'image/png';
		}
		return 'application/octet-stream';
	}

	/**
	 * Xử lý lưu lại thông tin phục vụ tracking
	 * @param {String} msg Thông tin cần log
	 * @author DNThang - 03.07.2019
	 */
	infoLogging(msg) {
		if (process.env.NODE_ENV === 'development' && msg) {
			console.debug(msg);
		}
	}

	/**
	 * Xử lý lưu lại thông tin phục vụ tracking
	 * @param {String} msg Thông tin cần log
	 * @author DNThang - 26.07.2019
	 */
	warnLogging(msg) {
		if (process.env.NODE_ENV === 'development' && msg) {
			console.warn(msg);
		}
	}

	/**
	 * Xử lý lưu lại thông tin phục vụ tracking
	 * @param {string | Exception} msg Thông tin cần lưu
	 */
	errorLogging(msg) {
		if (process.env.NODE_ENV === 'development' && msg) {
			console.error(msg);
		}
	}

	/**
	 * Lấy env
	 * @param {*} msg
	 */
	getNodeEnv(msg) {
		return process.env.NODE_ENV;
	}

	/**
	 * Hàm gọi func msToast
	 * @author NTMinh - 10.07.2019
	 * @modified DNThang - 13.12.2019: Bổ sung check tham số
	 */
	toast(parameters, hide = true) {
		if (parameters && parameters.text) {
			if (hide) {
				this.hideAllToast();
			}
			//Thay đổi toàn bộ kí tự đặc biệt `<`, `>` sang ký hiệu tương ứng để không bị ảnh hưởng việc hiển thị html
			// parameters.text = parameters.text.replace(/>/g, '&gt;').replace(/</g, '&lt;');
			msToast.msfunction(parameters);
		}
	}

	/**
	 * Kiểm tra array tồn tại phần tử item không
	 * @param {Array} collection
	 * @param {Object|String|Number} item
	 */
	contains(collection, item) {
		return collection.indexOf(item) !== -1;
	}

	/**
	 * Kiểm tra obj có phải Object không
	 * @param {Object} obj
	 */
	isObject(obj) {
		return obj !== null && obj && typeof obj === 'object' && !Array.isArray(obj);
	}

	/**
	 * Kiểm tra Object có phải là Empty hay không?
	 */
	objectIsEmpty(obj) {
		for (let key in obj) {
			if (obj.hasOwnProperty(key)) {
				return false;
			}
		}
		return true;
	}
	/**
	 * Kiểm tra dữ liệu tồn tại dữ liệu khác
	 */

	objectHasDataEmpty(obj, otherList = []) {
		for (let key in obj) {
			if (otherList.includes(key)) {
				continue;
			} else if (obj.hasOwnProperty(key) && obj[key]) {
				return false;
			}
		}
		return true;
	}

	/**
	 * Kiểm tra function
	 * @param {Object} func
	 */
	isCallable(func) {
		return typeof func === 'function';
	}
	/**
	 * Lấy độ cao của màn hình
	 *
	 */
	getWindowHeight() {
		let windowHeight = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
		return windowHeight;
	}

	getStyleDropByScreen() {
		if (window.innerHeight <= MSEnum.Screen.H900 && window.innerWidth >= MSEnum.Screen.W1600) {
			return {
				height: 550
			};
		}
		return null;
	}

	getScreenByWith() {
		if (window.innerWidth >= MSEnum.Screen.W1920) {
			return MSEnum.Screen.W1920;
		}
		if (window.innerWidth >= MSEnum.Screen.W1600) {
			return MSEnum.Screen.W1600;
		}
		if (window.innerWidth >= MSEnum.Screen.W1280) {
			return MSEnum.Screen.W1280;
		}
		return MSEnum.Screen.W1024;
	}

	/**Kiểm tra độ phân giải của màn hình */
	checkSmallScreen() {
		if (window.innerHeight <= 768 || window.innerWidth <= 1366) {
			return true;
		} else {
			return false;
		}
	}

	getScreenInfo() {
		const width = window.innerWidth;
		const height = window.innerHeight;
		let screenInfo = {
			width: width,
			height: height
		};
		if (width <= 1024 && height <= 768) {
			screenInfo.isScreen1024 = true;
		} else if (width <= 1280 && height <= 720) {
			screenInfo.isScreen1280x720 = true;
		} else if (width <= 1280 && height <= 800) {
			screenInfo.isScreen1280x800 = true;
		} else if (width <= 1366 && height <= 768) {
			screenInfo.isScreen1366 = true;
		} else if (width <= 1440 && height <= 900) {
			screenInfo.isScreen1440 = true;
		} else if (width <= 1600 && height <= 900) {
			screenInfo.isScreen1600 = true;
		} else if (width <= 1680 && height <= 1050) {
			screenInfo.isScreen1680 = true;
		} else if (width <= 1920 && height <= 1080) {
			screenInfo.isScreen1920 = true;
		} else if (width <= 1920 && height <= 1200) {
			screenInfo.isScreen1920x1200 = true;
		} else if (width <= 2560 && height <= 1440) {
			screenInfo.isScreen2560 = true;
		} else if (width <= 3840 && height <= 2160) {
			screenInfo.isScreen3840 = true;
		} else {
			screenInfo.isUnknownResolution = true;
		}

		return screenInfo;
	}

	getWidthByScreen(percent) {
		return (window.innerWidth / 100) * percent;
	}
	//#endregion Validation rules
	//Gộp url
	//CreatedBy: PDKIEN 24/07/2019
	//combineUrl("http://localhost","api","/v1")
	combineUrl() {
		if (arguments && arguments.length > 0) {
			let arr = [];
			for (let i = 0; i < arguments.length; i++) {
				let s = arguments[i],
					reg = new RegExp('^/*(.*?)/*$');
				arr.push(s.replace(reg, '$1'));
			}
			return arr.join('/');
		}
	}

	//Convert một object, array sang chuỗi Base64
	//CreatedBy: PDKIEN 30/7/2019
	convertToBase64(obj) {
		if (typeof obj != 'string') {
			obj = JSON.stringify(obj);
		}
		let buffer = Buffer.from(obj);
		let base64 = buffer.toString('base64');
		return base64;
	}

	/**
	 * Convert từ base64 sang object
	 * @param {Chuỗi đầu vào} base64String
	 * CreatedBy PDKIEN 09/10/2019
	 */
	convertFromBase64(base64String) {
		let buffer = Buffer.from(base64String, 'base64');
		let decode = buffer.toString('utf8');
		return decode;
	}

	/**
	 * Format string
	 * @param {string} inputString: chuỗi cần format
	 */
	stringFormat(inputString) {
		let formatted = inputString;
		for (let i = 1; i < arguments.length; i++) {
			let regexp = new RegExp('\\{' + i + '\\}', 'gi');
			formatted = formatted.replace(regexp, arguments[i]);
		}
		return formatted;
	}

	calculatorTotal(store, field) {
		let total = 0,
			data = store.getData();
		data.forEach(item => {
			total += Number(item[field]);
		});
		return total;
	}

	tableToExcel(table, name = 'name', fileName) {
		let uri = 'data:application/vnd.ms-excel;base64,',
			template =
				'<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40"><meta http-equiv="content-type" content="application/vnd.ms-excel;charset=UTF-8"><head><!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>{worksheet}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]--></head><body><table>{table}</table><script type="text/javascript">document.getElementsByClassName("wiget")forEach(item=>()=>{item.remove()})</script></body></html>',
			base64 = function(s) {
				return window.btoa(unescape(encodeURIComponent(s)));
			},
			format = function(s, c) {
				return s.replace(/{(\w+)}/g, function(m, p) {
					return c[p];
				});
			};

		if (!table.nodeType) table = document.getElementById(table);
		let myTable = table.cloneNode(true);
		myTable.querySelectorAll('.wiget').forEach(item => {
			item.remove();
		});
		myTable.querySelectorAll('.check-multi').forEach(item => {
			item.remove();
		});
		myTable.querySelectorAll('.outLeft').forEach(item => {
			item.remove();
		});
		let ctx = {
			worksheet: name || 'Worksheet',
			table: myTable.innerHTML
		};

		let downloadLink = document.createElement('a');
		document.body.appendChild(downloadLink);
		// Create a link to the file
		downloadLink.href = uri + base64(format(template, ctx));
		// Setting the file name
		downloadLink.download = fileName + '.xls';

		//triggering the function
		downloadLink.click();
	}

	/**
	 * Xuất toàn bộ dữ liệu grid ra excel
	 * (Dành riêng cho các grid phân trang client)
	 * @param {MsGrid} grd Đối tượng grid cần xử lý xuất khẩu
	 * CreatedBy DNThang - 14.04.2020
	 * ModifiedBy DNThang - 15.04.2020: Sửa để đáp ứng cho grid cõ show một số column đặc thù: row_number, margin, ...
	 */
	exportAllDataToExcell(grd, name = 'name', fileName) {
		if (!grd) {
			return;
		}

		let store = grd.store;
		if (store && store.getCount() > 0) {
			let table = grd.$refs.table,
				tableExport;

			if (grd.pagination && grd.getTotalPages > 1) {
				//Nếu nhiều dữ liệu hơn 1 page thì phải xử lý gen all data
				let tHead = table.tHead,
					tBody = table.tBodies[0],
					rows = tBody.rows;
				if (rows.length > 0) {
					let row = rows[0],
						columnx = grd.columnx,
						i = 0,
						j = 0;

					tableExport = document.createElement('table');
					tableExport.tHead = tHead.cloneNode(true);

					let bodyTemp = tableExport.createTBody(),
						idxCellDataStart = 0,
						idxCellRowNum = -1;

					//Lấy ra index cell đầu tiên của grid tương ứng với column
					for (j = 0; j < row.cells.length; j++) {
						let cellTemp = row.cells[j];

						if (cellTemp.classList.contains('ms-table--td')) {
							if (cellTemp.classList.contains('ms-grid-row-number')) {
								if (idxCellRowNum === -1) {
									idxCellRowNum = j;
								}
							} else {
								idxCellDataStart = j;
								break;
							}
						}
					}

					for (i = 0; i < store.getCount(); i++) {
						let eRow = row.cloneNode(true),
							cells = eRow.cells,
							dataItem = store.getData()[i];

						if (idxCellRowNum > -1) {
							cells[idxCellRowNum].innerText = i + 1;
						}

						for (j = 0; j < columnx.length; j++) {
							let cell = cells[j + idxCellDataStart],
								col = columnx[j],
								value = dataItem[col.dataField],
								formatType = col.dataFormat,
								forceFormat = false;

							if (forceFormat || col.enumName) {
								value = grd.$options.filters.formatData(value, {
									type: formatType,
									enumName: col.enumName
								});
							}

							cell.innerText = value;
						}

						bodyTemp.appendChild(eRow);
					}
				}
			} else {
				tableExport = table;
			}

			if (tableExport) {
				this.tableToExcel(tableExport, name, fileName);
			}
		}
	}

	/**
	 * Tạo UUID v7 (time-ordered UUID)
	 * UUID v7 sử dụng timestamp để có thể sắp xếp theo thời gian
	 * Tốt hơn cho database indexing và performance
	 * Create by: pvduong1 - 30/08/2019
	 * Modified to UUID v7 - 24/10/2025
	 */
	generateUUID() {
		return uuidv7().replace(/-/g, '');
	}

	/**
	 * Chuyển đổi ngày tháng truyền vào sang kiểu TimeStampWithoutTimeZone
	 * Created by: NVLAM 17/09/2019
	 */
	convertToTimeStampWithoutTimeZone(dateTime) {
		let year = dateTime.getUTCFullYear(),
			month = dateTime.getUTCMonth(),
			date = dateTime.getUTCDate(),
			hours = dateTime.getUTCHours(),
			minutes = dateTime.getUTCMinutes(),
			seconds = dateTime.getUTCSeconds(),
			miliseconds = dateTime.getUTCMilliseconds();
		return year + '-' + month + '-' + date + ' ' + hours + ':' + minutes + ':' + seconds + '.' + miliseconds;
	}

	/**
	 * Check column name có phải là column dạng số không?
	 * (Dựa vào naming)
	 * @param {String} colName Tên cột cần check
	 * DNThang - 21.10.2019
	 */
	isNumberColumn(colName) {
		if (
			colName.contains('price') ||
			colName.contains('amount') ||
			colName.contains('quantity') ||
			colName.contains('rate') ||
			colName.contains('minimum_stock')
		) {
			return true;
		}
		return false;
	}

	/**
	 * Lấy ra form root của control hiện tại
	 * @return {VueInstance/Element}
	 */
	getOwnerForm(control, isShowPopup = true) {
		let me = this,
			ret = control;
		if (control) {
			if (control.$el && control.$el.getVueInstance && control.$el.getVueInstance().isMsPopup) {
				ret = control.$el.getVueInstance();
				return ret;
			} else {
				let parent = control.$parent || control.$msOwner;
				if (
					parent === undefined ||
					(parent.$vnode && parent.$vnode.tag && parent.$vnode.tag.endsWith('-MsPopup')) ||
					(parent && parent.$el && parent.$el.id === 'app')
				) {
					ret = control;
					return ret;
				} else {
					ret = me.getOwnerForm(parent);
				}
			}
		}

		return ret;
	}

	/**
	 * focus control nhập liệu lỗi đầu tiên
	 * @param {*} scope : container chứa các control sẽ kiểm tra
	 */
	focusFirstControlError(scope) {
		let el = scope.querySelector('.ms-input-validate-error');
		if (el != null) {
			el.focus();
			el.select();
		}
	}
	isDefined(value) {
		if (value != undefined && value != null) {
			return true;
		}
		return false;
	}

	/**
	 * Tìm ra control Input đầu tiên theo scope truyền vào và cho phép sửa
	 * @param {Element} scope Parent cần tìm input
	 * @author DNThang - 31.10.2019
	 */
	findFirstControlInput(scope) {
		if (scope && scope.querySelector) {
			let input = scope.querySelector(
				'input:not([type=button]):not([type=radio]):not([type=checkbox]):not([readonly]):not([disabled]),textarea:not([readonly]):not([disabled])'
			);
			return input;
		}
		return;
	}

	/**
	 * Tự động focus vào control nhập liệu đầu tiên theo scope truyền vào
	 * @param {Element} scope Phạm vi cần xử lý focus
	 * @author DNThang - 31.10.2019
	 */
	focusFirstControlInput(scope) {
		const me = this;
		let input = me.findFirstControlInput(scope);
		if (input) {
			input.focus();

			if (typeof input.select === 'function') {
				input.select();
			}
		}
	}

	/**
	 * Tự động focus vào control nhập liệu đầu tiên theo scope truyền vào
	 * @param {Element} scope Phạm vi cần xử lý focus
	 * @author bnduc - 15.11.2019
	 */
	focusFirstControl(scope) {
		if (scope) {
			const me = this;

			let input;
			if (
				scope.type === 'text' ||
				(scope.hasAttribute && scope.hasAttribute('type') && scope.getAttribute('type') === 'text')
			) {
				input = scope;
			} else {
				input = me.findFirstControlFocus(scope);
			}

			if (input) {
				input.focus();
			}
		}
	}

	/**
	 * Tìm ra control Input đầu tiên theo scope truyền vào và cho phép sửa
	 * @param {Element} scope Parent cần tìm input
	 * @author bnduc - 15.11.2019
	 */
	findFirstControlFocus(scope) {
		if (scope && scope.querySelector) {
			let input = scope.querySelector('input:not([readonly]):not([disabled]),button:not([disable]),a,textarea');
			return input;
		}
		return;
	}

	/**
	 * focus vào control trước đó
	 */
	focusPreviewControl(curfocus) {
		let fn = function(parent) {
			let items = parent.querySelectorAll('input:not([disabled]),button:not([disable]),a');
			if (items[0] !== curfocus) {
				for (let i = 1; i < items.length; i++) {
					if (items[i] === curfocus) {
						for (let j = i - 1; j >= 0; j--) {
							//check item visible
							if (items[j].offsetParent) {
								items[j].focus();
								return;
							}
						}
						//reset focus to parent
						curfocus = parent;
					}
				}
			}
			fn(parent.parentElement);
		};

		fn(curfocus.parentElement);
	}

	/**
	 * focus vào control tiếp theo
	 */
	focusNextControl(curfocus, parent) {
		let fn = function(parent) {
			if (!parent) {
				return;
			}

			let items = parent.querySelectorAll(
				'input:not([disabled]),textarea:not([disabled]),button:not([disable]),a'
			);

			if (items[items.length - 1] !== curfocus) {
				for (let i = 1; i < items.length; i++) {
					if (items[i] === curfocus) {
						for (let j = i + 1; j < items.length; j++) {
							//check item visible
							if (items[j].offsetParent) {
								items[j].focus();
								return true;
							}
						}
					}
				}
			}

			return fn(parent.parentElement);
		};

		return fn(curfocus.parentElement);
	}

	/**
	 * Xử lý hiển thị dialog
	 * @author DNThang - 30.10.2019
	 * DNThang - 06.05.2020: Trong trường hợp owner là popup thì sẽ append vào content để không bị chìm ở dưới
	 */
	prepareForDialog(component, owner, options) {
		const me = this;
		if (owner && component) {
			let frmType = Vue.extend(component),
				frm = new frmType(options);

			frm.$mount();

			if (owner.isMsPopup) {
				let contentElement = owner.$refs.popupx; //owner.$el.querySelector('.ms-popup-content.ms-popup');
				contentElement.appendChild(frm.$el);
			} else {
				if (owner instanceof Element) {
					owner.appendChild(frm.$el);
				} else {
					owner.$el.appendChild(frm.$el);
				}
			}

			return frm;
		}

		return;
	}

	setLoading(isShowLoading, isLoading, timout = 45000) {
		const me = this;
		if (isShowLoading) {
			if (isLoading) {
				me.mask(timout);
			} else {
				me.unmask();
			}
		}
	}

	mask(timout = 45000) {
		const me = this;
		const appLoading = document.getElementById('loading-bg');
		const bgLoading = document.getElementById('loading-filler');

		if (appLoading) {
			appLoading.style.display = null;
		}

		if (bgLoading) {
			bgLoading.style.display = null;
		}

		//Ngắt loading sau 1 khoảng thời gian
		me.timeOutKeepLoading = setTimeout(() => {
			me.unmask();
		}, timout);
	}

	unmask() {
		const me = this;
		const appLoading = document.getElementById('loading-bg');
		const bgLoading = document.getElementById('loading-filler');
		if (appLoading) {
			appLoading.style.display = 'none';
		}
		if (bgLoading) {
			bgLoading.style.display = 'none';
		}

		//Loại bỏ timeout keep loading khi loading đã ngắt
		if (me.timeOutKeepLoading) {
			clearTimeout(me.timeOutKeepLoading);
		}
	}

	/**
	 * Xử lý khi focus control cuối cùng thì sẽ thực thi fn
	 */
	processUnfocusLastControl(e, fn) {
		if (e.which === 9 && !e.shiftKey) {
			let cur = e.target;
			let els = e.currentTarget.querySelectorAll('*');
			let flag = true;
			for (let i = 0; i < els.length; i++) {
				if (els[i] === cur) {
					flag = false;
					continue;
				}
				if (flag) {
					continue;
				}

				els[i].focus();
				if (els[i] === document.activeElement) {
					e.preventDefault();
					return;
				}
			}

			//focus first control
			e.preventDefault();
			fn();
		}
	}

	downloadLink(link) {
		// location.href = link;
		const downloadLink = document.createElement('a');
		downloadLink.href = link;
		downloadLink.click();
	}

	downloadSyncTool() {
		const me = this;
		if (typeof window === 'undefined') return;

		const externalServiceConfig =
			window.__env && window.__env.externalServiceConfig;
		const downloadLink =
			externalServiceConfig && externalServiceConfig.LinkDownloadTool;
		if (downloadLink) {
			me.previewLink(downloadLink);
		}
	}

	previewLink(link) {
		// location.href = link;
		// let a = document.getElementById('adownload');
		// a.href = link;
		// a.click('xxx');
		window.open(link, '_blank');
	}

	validateEmail(value) {
		try {
			let oRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			let result = value;
			if (!oRegex.test(result.trim())) {
				return false;
			}
		} catch (e) {
			return false;
		}
		return true;
	}

	/**
	 * Cá chép thông tin từ from cho to
	 * @param {*} from
	 * @param {*} to
	 */
	extend(from, to) {
		for (let i in from) {
			to[i] = from[i];
		}
	}

	/**
	 * Lấy ra thông tin Người dùng đăng nhập vào hệ thống (đã được lưu trữ trog store)
	 * (Bê từ session sang đây)
	 * @author DNThang - 05.12.2019
	 */
	getUserInfo() {
		return store.state.auth.userInfo;
	}

	/**
	 * Clone dữ liệu của mảng
	 * Sẽ tạo ra 1 mảng mới với dữ liệu được clone từ từng phần tử của mảng truyền vào
	 */
	cloneArrayData(inputArray) {
		let result = [];
		if (inputArray) {
			inputArray.forEach(function(item) {
				let temp = {};
				Object.assign(temp, item);
				result.push(temp);
			});
		}
		return result;
	}

	/**
	 * Clone dữ liệu của mảng model
	 * Sẽ tạo ra 1 mảng mới với dữ liệu được clone từ từng phần tử của mảng truyền vào
	 */
	cloneArrayModelData(inputArray) {
		let result = [];

		if (inputArray) {
			inputArray.forEach(function(item) {
				let temp = {};
				Object.assign(temp, item.$data);
				result.push(temp);
			});
		}

		return result;
	}

	shortkeyPushView(el) {
		window.shortkeyView.remove(el);
		window.shortkeyView.push(el);
	}

	shortkeyPopView(el) {
		window.shortkeyView.remove(el);
	}

	/**
	 * Kiểm tra 2 đối tượng có phải là cha con của nhau không
	 */
	hasParent(child, parent) {
		if (child.parentElement) {
			if (child.parentElement === parent) {
				return true;
			} else {
				return this.hasParent(child.parentElement, parent);
			}
		}

		return false;
	}

	/**
	 * Lấy đối tượng cha theo style class
	 */
	getParentByClass(child, className) {
		if (!child.parentNode) {
			return null;
		}

		if (this.hasClass(child.parentNode, className)) {
			return child.parentNode;
		} else {
			return this.getParentByClass(child.parentNode, className);
		}
	}

	/**
	 * Kiểm tra đối tượng có class style không
	 */
	hasClass(element, className) {
		return element.className.indexOf(className) > -1;
	}

	/**
	 * Ẩn tất cả các toast thông báo auto ẩn mà chưa đủ thời gian hiển thị để ẩn đi
	 * dùng cho tình huống khi nhấn cất/ghi sổ/bỏ ghi -> ẩn hết các thông báo cũ đi để thực hiện tại
	 */
	hideAllToast() {
		let els = document.querySelectorAll('.ms-toastfications');
		if (els && els.length > 0) {
			for (let i = 0; i < els.length; i++) {
				els[i].style.display = 'none';
			}
		}
	}

	/**
	 * Quay lại màn hình trước đó
	 */
	goBack(component, defaultPath) {
		let last = null,
			path = component.$route.path.toLowerCase();
		do {
			last = _routerHistory.pop();
		} while (last && last === path);
		component.$router.push(last || defaultPath || '/');
	}

	/**
	 * Hàm thông báo lỗi (nếu có refs truyền vào thì sẽ apply message vào refs)
	 * @param {Object} frm this
	 * @param {String} message tên lỗi
	 * @param {String} {optional - Nếu có thì sẽ border đỏ và focus control} refs tên refs control bị lỗi validate
	 * @author NCThanh1 - 13.09.2019
	 */
	onError(frm, message, refsName) {
		if (refsName) {
			const ref = frm.$refs[refsName];
			ref.$refs.provider.applyResult({
				errors: [message],
				valid: false,
				failedRules: {}
			});
			ref.$el.querySelector('input').focus();
		} else {
			frm.$ms.msgBox.showError(message);
		}
		return false;
	}

	/**
	 * Hàm kiểm tra số nhập liệu quá lớn
	 *
	 * Created by: pvduong1 - 14/02/2020
	 */
	checkTooBigNumber(number, message) {
		let MAX_VALUE = 99999999999999.9999;
		if (number >= MAX_VALUE) {
			if (message) {
				this.$ms.msgBox.showError(message);
			}
			return false;
		}
		return true;
	}

	//Build vue key cho data
	genVKeyValue(data) {
		if (data) {
			data.forEach(item => {
				if (!item.__vKeyValue) {
					item.__vKeyValue = this.generateUUID();
				}
			});
		}
	}

	//Chuyển dữ liệu thành dạng tree
	// Created by LTDAT(16.06.2020)
	nomalizeDataTree(data, idField, parentField) {
		const me = this;
		let dataTemp = cloneDeep(data),
			parentNodes = [];
		dataTemp.forEach((item, index) => {
			if (!item.hasOwnProperty('parentNode')) {
				item['parentNode'] = null;
			}
			if (!item.hasOwnProperty('childNodes')) {
				item['childNodes'] = [];
			}
			if (item[parentField]) {
				//Tìm cha
				let parent = dataTemp.find(o => {
					return o[idField] === item[parentField];
				});
				if (parent) {
					item.parentNode = parent;
					if (!parent.hasOwnProperty('isParent')) {
						parent['isParent'] = true;
					}
					parent.childNodes.push(item);
				} else {
					//Nếu không tìm thấy cha thì coi nó là cha luôn trường hợp lấy từ node khác node > 2
					parentNodes.push(item);
				}
			} else {
				parentNodes.push(item);
			}
		});
		return parentNodes;
	}

	/**
	 * Thực hiện ghép các node cha đi kèm với các node con luôn để thực hiện show ra ngay bên dưới node cha
	 * @param {[TreeModel]} dataResoures Danh sách các node cha cần sắp xếp
	 * @param {*} expanded Có Mở rộng ra không
	 * @param {*} level
	 */
	getBodyData(dataResoures, expanded, level = 1, childNodes = 'Childrens') {
		if (!dataResoures) {
			return [];
		}
		let data = [...dataResoures];
		let bodyData = [];
		data.forEach((row, index) => {
			const children = row[childNodes];
			const childrenLen = Object.prototype.toString.call(children).slice(8, -1) === 'Array' ? children.length : 0;

			row.level = level;
			Vue.set(row, 'isHide', false);
			Vue.set(row, 'expanded', expanded);
			// row.expanded = expanded;
			Vue.set(row, 'isSelected', false);
			Vue.set(row, '_isSelectedMultiple', false);

			bodyData.push(row);
			if (childrenLen > 0) {
				Vue.set(row, '_expandable', true);
				bodyData = bodyData.concat(this.getBodyData(children, expanded, level + 1));
			}
		});
		return bodyData;
	}

	getDataFromEnum(enumName, keyList) {
		let ret = [];
		let i18nEnum = i18n.t('i18nEnum');
		Object.keys(MSEnum[enumName]).forEach(key => {
			let item = null;
			if (keyList) {
				if (keyList.includes(MSEnum[enumName][key])) {
					item = {
						value: MSEnum[enumName][key],
						display: i18nEnum[enumName][key]
					};
					ret.push(item);
				}
			} else {
				item = {
					value: MSEnum[enumName][key],
					display: i18nEnum[enumName][key]
				};
				ret.push(item);
			}
		});
		return ret;
	}

	/**
	 * Lấy enum chính xác
	 */
	getDataFromEnumExactly(enumName, keyList) {
		let ret = [];
		let i18nEnum = i18n.t('i18nEnum');
		Object.keys(MSEnum[enumName]).forEach(key => {
			let item = null;
			if (keyList == MSEnum[enumName][key]) {
				item = {
					value: MSEnum[enumName][key],
					display: i18nEnum[enumName][key]
				};
				ret.push(item);
			}
		});
		return ret;
	}

	/**
	 * Hàm dùng để kiểm tra thay đổi giữa 2 dữ liệu
	 * @params newVal giá trị mới
	 * @params oldVal giá trị cũ
	 * @params ListCheckOut chứa các field bỏ qua không cần phải check
	 *  */
	checkChanges(newVal, oldVal, ListCheckOut) {
		let ret = false;
		if (typeof newVal == 'object' && typeof oldVal == 'object') {
			for (let field in newVal) {
				if (ListCheckOut && ListCheckOut.includes(field)) {
					continue;
				}
				if (newVal[field] != oldVal[field]) {
					ret = true;
					break;
				}
			}
		}
		return ret;
	}

	/**
	 * Lấy ra file popup component
	 */
	getPopupInstance(popupItems, instance) {
		const me = this;
		let formPopup = {};
		formPopup[instance.name] = popupItems[instance.name];
		if (!formPopup[instance.name]) {
			let form = Vue.extend(instance);
			popupItems[instance.name] = new form({
				i18n,
				store
			});
			popupItems[instance.name].$mount();
		}
		return popupItems[instance.name];
	}
	async getPopupInstanceAsync(popupItems, instance, propsData = {}, forceRender = false) {
		const me = this;
		if (popupItems[instance.name] && forceRender && popupItems[instance.name].$destroy) {
			popupItems[instance.name].$destroy();
		}
		// Cached instance có thể đã bị $destroy (do beforeDestroy set null chậm hơn caller khác đọc), phải bỏ qua cache nếu đã destroy
		if (popupItems[instance.name] && popupItems[instance.name]._isDestroyed) {
			popupItems[instance.name] = null;
		}
		if (typeof instance == 'function') {
			if (!popupItems[instance.name] || forceRender) {
				instance = await instance();
				instance = instance.default;
			}
		}

		let formPopup = {};
		formPopup[instance.name] = popupItems[instance.name];
		if (!formPopup[instance.name]) {
			let form = Vue.extend(instance);
			popupItems[instance.name] = new form({
				router,
				i18n: i18n,
				store,
				propsData: propsData
			});
			popupItems[instance.name].$mount();
			popupItems[instance.name].$once('hook:beforeDestroy', () => {
				popupItems[instance.name] = null;
			});
		}
		return popupItems[instance.name];
	}

	/**
	 * Created by pddang on 23/09/2026
	 * Kiểm tra điều kiện bật popup "Bạn có biết: Có thể rút ngắn Mẫu hóa đơn mặc định?" (PBI 677171)
	 * Bật khi đồng thời:
	 * - Còn trong thời gian hiển thị (hạn cấu hình tại window.__env.shortenTemplateGuide.expiredAt,
	 *   không cấu hình thì mặc định 31/03/2027)
	 * - User chưa chọn "Không hiển thị lại"
	 * - Đơn vị KHÔNG bật cả "Thiết lập mẫu hóa đơn" và "Ẩn mức thuế suất không phát sinh"
	 * @returns {Boolean} true nếu thỏa mãn điều kiện bật
	 */
	canShowShortenTemplateGuide() {
		//Hết hạn hiển thị: ưu tiên cấu hình, không có thì dùng mặc định 31/03/2027
		const DEFAULT_EXPIRED_AT = '2027-03-31T23:59:59';
		let expiredAt =
			window.__env && window.__env.shortenTemplateGuide && window.__env.shortenTemplateGuide.expiredAt
				? window.__env.shortenTemplateGuide.expiredAt
				: DEFAULT_EXPIRED_AT;
		if (moment().isAfter(moment(expiredAt))) return false;
		//User đã chọn "Không hiển thị lại"
		let user = store.getters['user/User'];
		if (user && user.IsWatchedShortenTemplateGuide) return false;
		//Đơn vị đã bật cả 2 thiết lập rút ngắn mẫu → không cần thông báo
		let settingSystem = store.getters['settingsystem/SettingSystem'];
		if (settingSystem) {
			let isSettingTemplate =
				settingSystem.IsSettingTemplate ||
				(settingSystem.TemplateSetting && settingSystem.TemplateSetting.ActiveSetting);
			let hiddenVatRate = settingSystem.TemplateSetting && settingSystem.TemplateSetting.HiddenVatRate;
			if (isSettingTemplate && hiddenVatRate) return false;
		}
		return true;
	}

	/**
	 * Created by pddang on 23/09/2026
	 * Hiển thị popup "Bạn có biết: Có thể rút ngắn Mẫu hóa đơn mặc định?" nếu thỏa mãn điều kiện.
	 * Gọi từ các hành động In/Tải xuống/Xem hóa đơn tại phân hệ Hóa đơn đầu vào/đầu ra (PBI 677171).
	 * Promise chỉ resolve khi user đóng popup (X / "Đã hiểu"): hành động In/Tải xuống phải await để chạy tiếp sau khi đóng,
	 * hành động Xem chi tiết gọi KHÔNG await để không bị chặn
	 */
	async showShortenTemplateGuideAsync() {
		const me = this;
		if (!me.canShowShortenTemplateGuide()) return;
		let instance = await me.getPopupInstanceAsync({}, () => import('@/views/Invoice/ShortenTemplateGuide.vue'));
		await instance.showPopup();
	}

	splitSecurityEmail(value) {
		let ret = '';
		ret = value.substr(0, 2) + '***' + value.substring(value.indexOf('@'), value.length);
		return ret;
	}

	splitSecurityPhone(value) {
		let ret = '*******';
		ret += value.substr(value.length - 3, value.length);
		return ret;
	}

	changeAlias(str) {
		if (str) {
			str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
			str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
			str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
			str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
			str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
			str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
			str = str.replace(/đ/g, 'd');
			str = str.replace(/Đ/g, 'D');
			str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A');
			str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E');
			str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I');
			str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O');
			str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U');
			str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y');
		}
		return str;
	}

	// tìm ra vị trí ký tùy từng file xml
	findSignLocation(submissionType) {
		const me = this;
		let result = MSEnum.SignLocation.Pay_RegistrationServiceTVAN_Sign;
		switch (submissionType) {
			case MSEnum.SubmissionType.Pay_RegistrationServiceTVAN:
			case MSEnum.SubmissionType.Pay_ChangeBankService:
				result = MSEnum.SignLocation.Pay_RegistrationServiceTVAN_Sign;
				break;
			case MSEnum.SubmissionType.Pay_StopServiceTVAN:
				result = MSEnum.SignLocation.Pay_StopServiceTVAN_Sign;
				break;
			case MSEnum.SubmissionType.Pay_ChangeInfoService:
				result = MSEnum.SignLocation.Pay_ChangeInfoService_Sign;
				break;
			case MSEnum.SubmissionType.Pay_StopBank:
				result = MSEnum.SignLocation.Pay_StopBank_Sign;
				break;
		}
		return result;
	}
	filterListObjByKey(data, field, value) {
		let ret = [];
		if (data && data.length > 0) {
			data.forEach(item => {
				if (
					item[field] &&
					item[field].toString() &&
					item[field]
						.toString()
						.toLowerCase()
						.contains(value.toLowerCase())
				) {
					ret.push(item);
				}
			});
		}
		return ret;
	}
	filterObjByKey(data, field, value) {
		if (
			data[field] &&
			data[field].toString() &&
			data[field]
				.toString()
				.toLowerCase()
				.contains(value.toLowerCase())
		) {
			return data;
		}
		return null;
	}

	PercentDate(from, to, now) {
		let timeRanger = new Date(to).getTime() - new Date(from).getTime();
		let timeFromToNow = new Date(now).getTime() - new Date(from).getTime();
		return (timeFromToNow / timeRanger) * 100;
	}
	getDictrictName(District) {
		if (District) {
			return District.replace('Quận', '')
				.replace('Huyện', '')
				.replace('Thành phố', '')
				.replace('Thị xã')
				.trim();
		}
		return null;
	}
	//Tìm kiếm item trong list
	//Created by LTDAT(16.06.2020)
	filterItemByText(queryText, data) {
		const me = this;
		let value = queryText,
			retItem = null;
		if (data) {
			retItem = data.find((item, _index) => {
				let ret = false;
				for (let key in item) {
					ret =
						item[key] &&
						item[key].toString() &&
						item[key]
							.toString()
							.toLowerCase()
							.contains(value.toLowerCase());
					if (ret) {
						return ret;
					}
				}
				if (ret) {
					return ret;
				}
			});
		}
		return retItem;
	}

	downloadFile(contenttype, base64, name) {
		const linkSource = `data:${contenttype};base64,${base64}`;
		const downloadLink = document.createElement('a');
		const fileName = name;
		downloadLink.href = linkSource;
		downloadLink.download = fileName;
		downloadLink.click();
	}

	clipboardCopy(text) {
		var textArea = document.createElement('textarea');
		textArea.value = text;
		document.body.appendChild(textArea);
		textArea.select();
		document.execCommand('Copy');
		textArea.remove();
	}

	downloadByBlob(fileContent, ContentType, name, exportContext = null) {
		//Kiểm tra xem có dữ liệu không thì mới tải
		if (!fileContent) return;

		//Không có dữ liểu tải về
		if (fileContent.byteLength == 0) {
			let isDownloadFile =
				ContentType.includes('zip') || ContentType.includes('xml') || ContentType.includes('pdf');

			// Kiểm tra context để emit event phù hợp
			if (exportContext === 'InvoiceList') {
				// Tab "Danh sách hóa đơn" - emit event riêng
				EventBusGlobal.$emit(GlobalEventName.EmptyDataDownloadListInv);
			} else {
				// Các trường hợp khác - giữ nguyên logic cũ
				EventBusGlobal.$emit(GlobalEventName.EmptyDataDownload, isDownloadFile);
			}
			return;
		}

		//Tải tệp tin
		let blob = new Blob([fileContent], { type: ContentType });
		let url = URL.createObjectURL(blob);
		const downloadLink = document.createElement('a');
		const fileName = name;
		downloadLink.href = url;
		downloadLink.download = fileName;
		downloadLink.click();
		setTimeout(() => {
			URL.revokeObjectURL(url);
		});
	}

	downloadByBlobReport(fileContent, ContentType, name) {
		//Kiểm tra xem có dữ liệu không thì mới tải
		if (!fileContent) return;

		//Không có dữ liểu tải về
		if (fileContent.byteLength == 0) {
			EventBusGlobal.$emit(GlobalEventName.EmptyDataDownloadReport);
			return;
		}

		//Tải tệp tin
		let blob = new Blob([fileContent], { type: ContentType });
		let url = URL.createObjectURL(blob);
		const downloadLink = document.createElement('a');
		const fileName = name;
		downloadLink.href = url;
		downloadLink.download = fileName;
		downloadLink.click();
		setTimeout(() => {
			URL.revokeObjectURL(url);
		});
	}

	// Tự động mở file
	openByBlob(fileContent, ContentType, name) {
		//Kiểm tra xem có dữ liệu mở
		if (!fileContent) return;
		let blob = new Blob([fileContent], { type: ContentType });
		let url = URL.createObjectURL(blob);
		window.open(url);
	}

	/**
	 * Trả về đơn vị đang làm việc dạng `${subscriberId}/${organizationId}`
	 * @returns
	 */
	getSubParam() {
		let subscriberId = store.getters['organization/getSubscriberWorking'].Id,
			organizationId = store.getters['organization/getOrganizationWorking'].Id,
			subParam = `${subscriberId}/${organizationId}`;
		return subParam;
	}

	/**
	 * Trả về đơn vị đang làm việc dạng `${subscriberId}/${organizationId}`
	 * @param {*} orgId
	 * @returns
	 */
	getSubParamCustom(orgId) {
		let subscriberId = store.getters['organization/getSubscriberWorking'].Id,
			organizationId = orgId,
			subParam = `${subscriberId}/${organizationId}`;
		return subParam;
	}

	/**
	 * Trả về SubscriberId
	 * @returns
	 */
	getSubParamSubOnly() {
		return store.getters['organization/getSubscriberWorking'].Id;
	}

	/**
	 * Trả về OrganizationId
	 * @returns
	 */
	getOrgParamOrgOnly() {
		return store.getters['organization/getOrganizationWorking'].Id;
	}

	//Lấy các chi nhánh cha con của Org Working
	getParentOfOrganizationWorking() {
		//Lấy đơn vị đang làm việc
		let orgInfo = store.getters['organization/getOrganizationWorking'];
		if (!orgInfo) return [];

		//Lấy danh sách đơn vị được cấp quyền
		let orgs = store.getters['organization/getAllOrganizations'];
		let tempOrganizations = orgs;
		if (!orgs || orgs.length <= 0) return [];

		let results = [];
		tempOrganizations.forEach(org => {
			this.getParentOfOrganization(results, orgs, orgInfo);
		});
		return results;
	}

	/**
	 * Đang sử dụng ElasticSearch hay không
	 * @returns
	 */
	getIsUseElastic() {
		return store.getters['organization/getSubscriberWorking'].IsUseElastic;
	}

	helpLink(path, isEnpointKB = true) {
		let helpLink = window.__env.authConfig.help_url;
		helpLink = isEnpointKB ? `${helpLink}/kb` : helpLink;
		if (!path) {
			path = '';
		}
		window.open(`${helpLink}${path}`, '_blank');
	}

	getHelpLink(path = '', isEnpointKB = true) {
		let helpLink = window.__env.authConfig.help_url;
		return isEnpointKB ? `${helpLink}/kb${path}` : `${helpLink}${path}`;
	}

	removeInsAfterDestroy(parent, instance) {
		let removeInstance = () => {
			if (instance) {
				instance.$destroy();
				instance.$el.remove();
			}
		};
		parent.$once('hook:beforeDestroy', removeInstance);
		instance.$once('hook:beforeDestroy', () => {
			parent.$off('hook:beforeDestroy', removeInstance);
		});
	}

	sortDataInfoAccept(lstData, lstKindChange = []) {
		const me = this;
		if (!lstData || lstData.length <= 0 || !lstKindChange || lstKindChange.length <= 0) return lstData;

		//Lọc dữ liệu
		lstData = lstData.filter(x => lstKindChange.includes(x.KindChange));
		if (!lstData || lstData.length <= 0) return lstData;

		//Chọn dữ liệu cần so sánh
		const getComparisonDate = item => {
			return item.StartDate || item.CreatedDate;
		};
		return lstData.sort((a, b) => {
			const dateA = getComparisonDate(a);
			const dateB = getComparisonDate(b);
			return me.isAfterDate(dateA, dateB);
		});
	}

	getOrgWorkingUpdate(organizationUnits, res) {
		const me = this;
		if (!organizationUnits || organizationUnits.length <= 0 || !res) return null;

		for (let index = 0; index < organizationUnits.length; index++) {
			let item = organizationUnits[index];
			if (!item) continue;

			item.IsWorking = item.Id == res.Id;
			if (item.Id == res.Id) {
				if (res.InvoiceCount >= 0 && res.OutInvoiceCount >= 0) {
					return res;
				}
				return item;
			}

			if (item.Childrens && item.Childrens.length > 0) {
				let org = me.getOrgWorkingUpdate(item.Childrens, res);
				if (org) return org;
			}
		}
	}

	/**
	 * Tìm tổ chức theo Id trong danh sách tổ chức (duyệt đệ quy cả con)
	 * @param {Array} organizationUnits Danh sách tổ chức cần tìm
	 * @param {String} organizationId Id tổ chức cần tìm
	 * @returns {Object|null} Tổ chức tìm thấy hoặc null
	 * Created by pddang on 24/08/2026
	 */
	findOrganizationById(organizationUnits, organizationId) {
		const me = this;
		// Không có danh sách tổ chức hoặc không có organizationId thì trả về null
		if (!organizationUnits || organizationUnits.length <= 0 || !organizationId) return null;
		for (let index = 0; index < organizationUnits.length; index++) {
			let organization = organizationUnits[index];
			if (organization && organization.Id == organizationId) return organization;
			let childOrganization = organization && me.findOrganizationById(organization.Childrens, organizationId);
			if (childOrganization) return childOrganization;
		}
		return null;
	}

	getOrgWorking(organizationUnits) {
		const me = this;
		if (!organizationUnits || organizationUnits.length <= 0) return null;
		for (let index = 0; index < organizationUnits.length; index++) {
			let item = organizationUnits[index];
			if (!item) continue;
			if (item.IsWorking) {
				return item;
			}

			if (item.Childrens && item.Childrens.length > 0) {
				let org = me.getOrgWorking(item.Childrens);
				if (org) return org;
			}
		}
	}

	/**
	 * Lấy dữ liệu cha con theo chi nhánh
	 */
	getParentOfOrganization(results, orgs, org) {
		if (!results || !orgs) return;
		//Thêm bản ghi hiện tại vào danh sách nếu chưa tồn tại
		let checkExist = results.some(x => x.Id == org.Id);
		if (!checkExist) {
			//Các org con
			if (org.Childrens && org.Childrens.length > 0) {
				org.Childrens.forEach(children => {
					results.push(children);
				});
			}
			results.push(org);
		}

		//Nếu có cha
		if (org.ParentId) {
			var orgParent = orgs.filter(n => n.Id == org.ParentId)[0];
			if (orgParent) {
				this.getParentOfOrganization(results, orgs, orgParent);
			}
		}
	}

	setDataSortRemember(screenName, configSortGrid) {
		const me = this;

		//Lấy giá trị sorts trong local storage
		var sortsInLocalStorage = localStorage.getItem(KeySortInLocalStorage);
		let configSort = !sortsInLocalStorage ? {} : JSON.parse(sortsInLocalStorage);

		//Điều kiện sắp xếp mặc định
		let sortCondition = {
			sortStatus: configSortGrid.mode,
			col: configSortGrid.col
		};

		//Lấy thông tin chi nhánh/phòng ban đang làm việc
		let organizationWorking = store.getters['organization/getOrganizationWorking'];
		if (!organizationWorking || !organizationWorking.Id) return sortCondition;

		//Lưu điều kiện sort xuống local storage
		let screenNameSort = `${screenName + organizationWorking.Id}`;
		configSort[`${screenNameSort}`] = {
			mode: configSortGrid.mode,
			col: configSortGrid.col
		};
		localStorage.setItem(KeySortInLocalStorage, JSON.stringify(configSort));

		//Trả kết quả đã xử lý
		return sortCondition;
	}
	/**
	 * Lây trạng thái sắp xếp của ds
	 * @param {*Tên danh sách} screenName
	 */
	getDataSortRemember(screenName) {
		//Điều kiện sắp xếp mặc định
		let sortCondition = {
			sortStatus: MSEnum.SortStatus.none,
			col: ''
		};

		//Lấy thông tin chi nhánh/phòng ban đang làm việc
		let organizationWorking = store.getters['organization/getOrganizationWorking'];
		if (!organizationWorking || !organizationWorking.Id) return sortCondition;

		//Lấy giá trị sorts trong local storage
		var sortsInLocalStorage = localStorage.getItem(KeySortInLocalStorage);
		if (!sortsInLocalStorage) return sortCondition;

		//Chuyển đổi chuỗi sang đối tượng json
		let configSort = JSON.parse(sortsInLocalStorage);
		if (!configSort) return sortCondition;

		//Cập nhật lại điều kiện sort lưu trong local storage
		let screenNameSort = `${screenName + organizationWorking.Id}`;
		if (configSort[screenNameSort]) {
			sortCondition = {
				sortStatus: configSort[screenNameSort].mode,
				col: configSort[screenNameSort].col
			};
		}

		return sortCondition;
	}
	getSortParams(sortCondition) {
		//Điều kiện lọc mặc định
		if (!sortCondition || !sortCondition.sortStatus || sortCondition.sortStatus == MSEnum.SortStatus.none)
			return '';

		//Điều kiện lọc khác
		return `${sortCondition.col.dataField} ${sortCondition.sortStatus == MSEnum.SortStatus.desc ? 'DESC' : 'ASC'}`;
	}

	getNatureItem(item, isSupportSpecificProduct) {
		let me = this,
			res = '',
			kind = item.Kind;
		switch (kind) {
			case MSEnum.KindND123Enum.Info:
				res = i18n.t('i18nInvoice.Template.ItemInfo');
				break;
			case MSEnum.KindND123Enum.Promotion:
				res = i18n.t('i18nInvoice.Template.ItemPromotion');
				break;
			case MSEnum.KindND123Enum.Discount:
				res = i18n.t('i18nInvoice.Template.ItemDiscount');
				break;
			case MSEnum.KindND123Enum.Noted:
				res = i18n.t('i18nInvoice.Template.ItemNoted');
				break;
			case MSEnum.KindND123Enum.SpecificProduct:
				if (!isSupportSpecificProduct) {
					res = '';
				} else {
					res = i18n.t('i18nInvoice.Template.ItemSpecificProduct');
				}
				break;
		}
		return res;
	}

	vatRateShowND123(item, invoiceInfo) {
		const me = this;
		return me.vatRateBaseDisplay(item, invoiceInfo);
	}

	vatRateShowND70(item, invoiceInfo) {
		const me = this;
		if (item.VatRateType == MSEnum.VatRateEnum.FinancialLeasingVat) {
			//Không phải hóa đơn tài chính k hiển thị
			if (!invoiceInfo.IsFinancialLease) return '';
			return 'CTTC';
		}
		return me.vatRateBaseDisplay(item, invoiceInfo);
	}

	vatRateBaseDisplay(item, invoiceInfo) {
		const me = this;
		let vatShow = '';
		switch (item.VatRate) {
			case -1:
				vatShow = 'KCT';
				break;
			case -2:
				vatShow = 'KKKNT';
				break;
			case -3:
				vatShow = 'KHAC';
				break;
			case -4:
				vatShow = 'xxx';
				break;
			//KTPVDC/KTGTT theo NĐ254 — hiển thị mã riêng, không gộp vào thuế khác
			case MSEnum.VatRate.KTPVDC:
				vatShow = 'KTPVDC';
				break;
			case MSEnum.VatRate.KTGTT:
				vatShow = 'KTGTT';
				break;
			default:
				if (item.VatRate >= 0) {
					if (item.IsVatOther && invoiceInfo && !invoiceInfo.IsTaxReduction) {
						vatShow = 'KHAC: ' + item.VatRate + '%';
					} else if (invoiceInfo.IsInvoiceTiktok && item.VatAmount == 0) {
						vatShow = '';
					} else {
						vatShow = item.VatRate + '%';
					}
				}
				break;
		}
		return vatShow;
	}

	vatRateShowND254(item, invoiceInfo) {
		const me = this;
		if (item.VatRateType == MSEnum.VatRateEnum.FinancialLeasingVat) {
			//Không phải hóa đơn tài chính k hiển thị
			if (!invoiceInfo.IsFinancialLease) return '';
			return 'CTTC';
		}
		//Dùng chung base display
		return me.vatRateBaseDisplay(item, invoiceInfo);
	}

	/**
	 * Bổ sung style cho iframe content mail
	 */
	addStyleToIframe(contentMail) {
		const me = this;
		if (contentMail) {
			const headPrev =
				'<head>\r\n\r\n    <meta http-equiv="content-type" content="text/html; charset=UTF-8">\r\n  </head>';
			const headNext =
				'<head>\r\n\r\n    <meta http-equiv="content-type" content="text/html; charset=UTF-8">' +
				"<style type='text/css'> html::-webkit-scrollbar {width: 16px !important;height: 16px !important;}" +
				'html::-webkit-scrollbar-thumb {min-height: 50px;-webkit-border-radius: 8px;border: 4px solid #fff;background-color: #ced0d4;}' +
				'html::-webkit-scrollbar-track {background: transparent !important;}' +
				'</style>\r\n  </head>';
			contentMail = me.replaceAllExt(contentMail, headPrev, headNext);
		}
		return contentMail;
	}

	/**
	 * Chuẩn hóa text để tạo tên file
	 * @param {String} text
	 */
	normalizeFileNameText(text) {
		const me = this;
		if (!text) return '';
		let result = me.removeAccents(text.toLowerCase());
		result = result.replace(/[\r\n]+/g, ''); // Bỏ dấu xuống dòng
		result = result.replace(/[^\w\s]|_/gi, ''); // Bỏ ký tự đặc biệt
		result = result.replace(/\s/g, ''); // Bỏ khoảng trắng
		return result;
	}

	getNameFileSetting(settingSystem, invoiceInfo, isCheckThirdParty) {
		const me = this;
		let fileName = '';
		if (
			settingSystem &&
			settingSystem.OrderSettingInvoiceName &&
			settingSystem.OrderSettingInvoiceName.length > 0
		) {
			let arrName = [];
			for (let item of settingSystem.OrderSettingInvoiceName) {
				if (item == MSEnum.SettingSystemInvoiceName.SellerTaxCode && invoiceInfo.SellerTaxCode) {
					arrName.push(invoiceInfo.SellerTaxCode);
				} else if (item == MSEnum.SettingSystemInvoiceName.TemplateNo && invoiceInfo.TemplateNo) {
					arrName.push(invoiceInfo.TemplateNo);
				} else if (item == MSEnum.SettingSystemInvoiceName.Series && invoiceInfo.Series) {
					arrName.push(invoiceInfo.Series);
				} else if (item == MSEnum.SettingSystemInvoiceName.InvoiceNo && invoiceInfo.InvoiceNo) {
					arrName.push(invoiceInfo.InvoiceNo);
				} else if (item == MSEnum.SettingSystemInvoiceName.InvoiceDate && invoiceInfo.InvoiceDate) {
					let invoiceDate = new Date(invoiceInfo.InvoiceDate);
					//format ddMMyyyy
					let dateString = `${me.padTo2Digits(invoiceDate.getDate())}${me.padTo2Digits(
						invoiceDate.getMonth() + 1
					)}${me.padTo2Digits(invoiceDate.getFullYear())}`;
					arrName.push(dateString);
				} else if (item == MSEnum.SettingSystemInvoiceName.SupplierName && invoiceInfo.SellerName) {
					arrName.push(me.normalizeFileNameText(invoiceInfo.SellerName));
				} else if (item == MSEnum.SettingSystemInvoiceName.BuyerName && invoiceInfo.BuyerName) {
					// Tên người mua: lấy lên Tên người mua trên hóa đơn (BuyerName) sau khi bỏ qua các dấu thanh tiếng Việt và dấu xuống dòng.
					arrName.push(me.normalizeFileNameText(invoiceInfo.BuyerName));
				} else if (item == MSEnum.SettingSystemInvoiceName.BuyerTaxCode && invoiceInfo.BuyerTaxCode) {
					// Mã số thuế người mua: lấy lên Mã số thuế mua trên hóa đơn (BuyerTaxCode) sau khi bỏ qua các dấu thanh tiếng Việt và dấu xuống dòng.
					arrName.push(invoiceInfo.BuyerTaxCode);
				} else if (item == MSEnum.SettingSystemInvoiceName.InvoiceNote && invoiceInfo.Noted) {
					// Ghi chú: lấy lên nội dung ghi chú trên hóa đơn sau khi bỏ qua các dấu thanh tiếng Việt và dấu xuống dòng.
					arrName.push(me.normalizeFileNameText(invoiceInfo.Noted));
				}
			}
			fileName = arrName.join('_');
		}

		// Xóa các ký tự cuối nếu tên file lớn hơn 100 ký tự (Do tên file pdf và xml chỉ có thể đặt tối đa 100 ký tự)
		if (fileName.length > window.__env.MaxLengthFileNameDownload) {
			fileName = fileName.substring(0, window.__env.MaxLengthFileNameDownload);
		}
		if (isNullOrEmpty(fileName)) {
			fileName = isCheckThirdParty ? 'hoa_don' : 'hoa_don_dau_vao';
		}
		return fileName;
	}

	/**
	 * Sinh ra hàm này do khi xem chi tiết hóa đơn trên SME bị lỗi hàm replaceAll
	 * @param {*} str
	 * @param {*} find
	 * @param {*} replace
	 * @returns
	 */
	replaceAllExt(str, find, replace) {
		try {
			if (!str) return str;
			while (str && str.contains(find)) {
				str = str.replace(find, replace);
			}
		} catch (ex) {
			console.log(ex);
		}
		return str;
	}

	removeAccents(str) {
		const me = this;
		str = me.replaceAllExt(str, 'đ', 'd');
		return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
	}

	padTo2Digits(num) {
		if (!num && num != 0) return '';
		return num.toString().padStart(2, '0');
	}

	formatDigitNumber(number = 0, digit = 2, localize = 'en-US') {
		if (number <= 0) return '';
		return number.toLocaleString(localize, {
			minimumIntegerDigits: digit,
			useGrouping: false
		});
	}

	encodeHTML(str) {
		try {
			if (!str) return str;
			return str.replace(/[\u00A0-\u9999<>\&]/gim, function(i) {
				return '&#' + i.charCodeAt(0) + ';';
			});
		} catch (e) {
			return str;
		}
	}

	isShowPermissionWork(typeWork, lstWorkPermission) {
		let exist = lstWorkPermission.find(x => x == typeWork);
		if (exist > 0) {
			return true;
		} else {
			return false;
		}
	}

	formatEnum(value, enumName) {
		return msFormat.formatEnum(value, enumName);
	}
	/**
	 *
	 * @param {*} vueInstance
	 * @param {*} methods
	 * @param {*} params
	 */
	async emitPromise(vueInstance, methods, params) {
		if (vueInstance && vueInstance._events) {
			let listener = vueInstance._events[methods];
			if (listener && listener.length > 0) {
				for (let i = 0; i < listener.length; i++) {
					await listener[i](params);
				}
			}
		}
	}
	/**
	 * Thêm dữ liệu vào mảng
	 * @param {*} arr Danh sách dữ liệu
	 * @param {*} index vị trí cần thêm vào danh sách trên
	 * @param  {...any} item các dữ liệu cần thêm vào vị trí index
	 */
	ArryInsertByIndex(arr, index, ...items) {
		arr.splice(index, 0, ...items);
	}
	/**
	 * Hàm check chuỗi có phải các số hay không
	 * @param {Chuỗi các số} number(string)
	 * @returns
	 */
	isNumeric(number) {
		if (typeof number != 'string') return false;
		return !isNaN(number) && !isNaN(parseFloat(number));
	}

	/**
	 * Lưu vết link connect lần gần nhất
	 * Của 2 nhà cung cấp VNPT, EVN
	 * data key là enum của NCC, value là link kết nối gần nhất
	 */
	setLinkWebsite(data, isDelete = false) {
		const me = this;
		if (!data) return;
		let dataStorage = localStorage.getItem(KeyLinkWebSite);
		let dataConnect = !dataStorage ? {} : JSON.parse(dataStorage);
		if (data.key) {
			if (isDelete) {
				delete dataConnect[data.key];
			} else {
				//Cập nhật dữ liệu
				dataConnect[data.key] = data.value;
			}
		}

		localStorage.setItem(KeyLinkWebSite, JSON.stringify(dataConnect));
	}

	/**
	 * Lấy link kết nối của nhà cung cấp đã kết nối trước đó
	 * Phục vụ cho nhà cung cấp VNPT, EVN
	 */
	getLinkConnectPrev(key) {
		if (!key) return null;
		let dataStorage = localStorage.getItem(KeyLinkWebSite);
		let dataConnect = !dataStorage ? null : JSON.parse(dataStorage);
		if (!dataConnect) return null;
		let dataResult = dataConnect[key];
		if (dataResult) return dataResult;
		return null;
	}

	/*
	 * Key lưu trữ link website trước đó của chi nhánh trên storage
	 */
	getKeyLinkWebsiteConnect(type, orgId) {
		return `${type}_${orgId}`;
	}

	/**
		Hàm mã hóa dữ liệu theo thuật toán CrytoJs phục vụ mã hóa tài khoản mật khẩu kết nối NCC Honda
	 */
	EncodeCryptoJS(data, keyEncCrypto = '8080808080808080', ivEncCrypto = '8080808080808080') {
		var key = CryptoJS.enc.Utf8.parse(keyEncCrypto);
		var iv = CryptoJS.enc.Utf8.parse(ivEncCrypto);
		var encryptedData = CryptoJS.AES.encrypt(CryptoJS.enc.Utf8.parse(data), key, {
			keySize: 128 / 8,
			iv: iv,
			mode: CryptoJS.mode.CBC,
			padding: CryptoJS.pad.Pkcs7
		});
		return encryptedData;
	}

	/**
	 * Check định dạng file pdf
	 */
	isFormatPdf(extension) {
		if (!extension) return false;
		let extentionsPdf = 'pdf,jpg,jpeg,jfif,pjpeg,pjp,png,apng,gif,bmp,avif,svg,webp';
		let extentions = extentionsPdf.split(',');
		if (extentions && extentions.length > 0) {
			let extentionsFilter = extentions.filter(x => extension.toLowerCase() == x.trim());
			if (extentionsFilter && extentionsFilter.length > 0) return true;
		}
		return false;
	}

	/**
	 * Check định dạng file xml
	 */
	isFormatXml(extension) {
		if (!extension) return false;
		let extentionsXml = 'xml,inv';
		let extentions = extentionsXml.split(',');
		if (extentions && extentions.length > 0) {
			let extentionsFilter = extentions.filter(x => extension.toLowerCase() == x);
			if (extentionsFilter && extentionsFilter.length > 0) return true;
		}
		return false;
	}

	/**
	 * Hiển thị với HĐ không mã gửi theo HT BTH
	 * Hoặc hđ NĐ123: là HHDV hoặc chiết khấu thương mại
	 */
	isShowDataColumnDetail(item, invoiceInfo) {
		let check =
			item.IsItemAndService ||
			item.Kind == MSEnum.KindND123Enum.Discount ||
			item.Kind == MSEnum.KindND123Enum.Noted ||
			item.Kind == MSEnum.KindND123Enum.Promotion;
		if (invoiceInfo.IsSpecialInvoice) return true;
		if (check) return true;
		//HĐ không mã gửi theo HT bảng tổng hợp(được thiết lập tải về phần mềm)
		if (
			invoiceInfo &&
			invoiceInfo.IsInvoiceSendBySumary &&
			invoiceInfo.InvoiceCodeStatus == MSEnum.InvoiceCodeStatus.WithoutCode
		)
			return true;
		return false;
	}

	invoiceWithoutCodeSendBySumary(invoiceInfo) {
		return (
			invoiceInfo &&
			invoiceInfo.IsInvoiceSendBySumary &&
			invoiceInfo.InvoiceCodeStatus == MSEnum.InvoiceCodeStatus.WithoutCode
		);
	}

	setStateNotificationsInbot(key, state) {
		const me = this;
		if (!key || !state) return;
		let stateNotifications = localStorage.getItem(KeyNotificationsInbot);
		stateNotifications = !stateNotifications ? {} : JSON.parse(stateNotifications);
		stateNotifications[`${key}`] = state;
		localStorage.setItem(KeyNotificationsInbot, JSON.stringify(stateNotifications));
	}

	getStateNotificationsInbot(key) {
		const me = this;
		let stateNotifications = localStorage.getItem(KeyNotificationsInbot);
		stateNotifications = !stateNotifications ? {} : JSON.parse(stateNotifications);
		return stateNotifications[`${key}`];
	}

	//Thiết lập ngôn ngữ khi người dùng đi từ meInvoice vào
	async initSupportLanguage() {
		const me = this;
		let lang = 'vi';
		if (window.__env && window.__env.authConfig && window.__env.authConfig.isMultipleLanguage) {
			lang = me.getLanguageFromMeinvoice();
		} else {
			lang = me.getLanguageFromStorage();
		}
		//Nếu ngôn ngữ thay đổi hoặc chưa load messages thì cập nhật
		if (i18n && (i18n.locale !== lang || !loadedLocales.includes(lang))) {
			await i18n.updateLocale(lang);
			me.setLanguageToStorage(lang);
		}
	}

	//Hàm kiểm tra xem ứng dụng hỗ trợ ngôn ngữ đó không
	isSupportLanguage(lang) {
		const me = this;
		return lang === 'vi' || lang === 'en';
	}

	//Trả ra ngôn ngữ mà ứng dụng hỗ trợ, nếu không có trả về mặc định 'vi'
	getSupportLanguage(lang) {
		const me = this;
		if (me.isSupportLanguage(lang)) return lang;
		return 'vi';
	}

	//Lưu ngôn ngữ vào Storage
	setLanguageToStorage(lang) {
		const me = this;
		//Nếu không nằm trong danh sách ngôn ngữ hỗ trợ thì gán mặc định
		lang = me.getSupportLanguage(lang);
		//Cập nhật ngôn ngữ
		localStorage.setItem(KeyCulture, lang);
		//Cập nhật sang mei
		localStorage.setItem(KeyLanguageMeinvoice, me.mappingLanguageToMeinvoice(lang));
	}

	//Lấy ngôn ngữ vào Storage
	getLanguageFromStorage() {
		const me = this;
		//Lấy ngôn ngữ trong Storage
		var lang = localStorage.getItem(KeyCulture);
		return me.getSupportLanguage(lang);
	}

	/**
	 * Lấy thông tin language meinvoice lưu trên localstorage
	 */
	getLanguageFromMeinvoice() {
		const me = this;
		let lang = localStorage.getItem(KeyLanguageMeinvoice);
		if (!lang) {
			lang = me.getLanguageFromStorage();
		}
		if (lang === 'en-US' || lang === 'en') return 'en';
		return 'vi';
	}

	mappingLanguageToMeinvoice(inbotLang) {
		if (inbotLang === 'en-US' || inbotLang === 'en') return 'en-US';
		return 'vi-VN';
	}

	convertData(obj) {
		for (const key in obj) {
			let value = obj[key];
			obj[key] = value.charAt(0).toUpperCase() + value.substr(1);
		}
		return obj;
	}

	getStatusInfoText(checkingResult) {
		if (!i18n.locale || i18n.isVietnamese) {
			return checkingResult.StatusInfoText;
		} else {
			let actives = i18n.t('i18nSupplier.ActiveSeller', 'vi');
			for (const key in actives) {
				if (checkingResult.StatusInfo && checkingResult.StatusInfo.includes(actives[`${key}`])) {
					return i18n.t('i18nSupplier.ActiveSeller', `${i18n.locale}`)[`${key}`];
				}
			}
		}
		return checkingResult.StatusInfoText;
	}

	compareTwoDate(date1, date2) {
		let d1 = moment(new Date(date1)).format('DD/MM/YYYY');
		let d2 = moment(new Date(date2)).format('DD/MM/YYYY');
		return d1 == d2;
	}

	formatDate(date, format = 'YYYY/MM/DD') {
		return moment(new Date(date)).format(format);
	}

	convertStringToDate(dateString = '', format = 'DD/MM/YYYY') {
		return moment(dateString, format).toDate();
	}

	/**
	 * Distinct Array Object
	 */
	distinctArray(arr, key) {
		if (!arr || !key) return arr;
		return [...new Map(arr.map(item => [item[`${key}`], item])).values()];
	}

	/**
	 * sửa thành tối đa 1000 hóa đơn
	 * @param {*} instance
	 * @param {*} selected
	 * @param {*} maxSelected
	 * @returns
	 */
	async validateSelected(instance, selected, maxSelected = 100) {
		if (!selected || selected.length <= maxSelected) return true;
		let waningText = `${instance.$t('i18nInvoice.InvoiceInbot').InvoiceSelectedWarning(maxSelected)}`;
		let options = {
			btnConfig: [{ key: 'Close', target: 'Right', text: `${i18n.t('i18nCommon.Close')}`, focus: true }],
			title: `${i18n.t('i18nCommon.Warning')}`,
			message: instance.isInbox
				? `${instance.$t('i18nInbox').InboxSelectedWarning(maxSelected)}`
				: `${instance.$t('i18nInvoice.InvoiceInbot').InvoiceSelectedWarning(maxSelected)}`,
			hiddenBorderHeader: true,
			width: '380px',
			classMessageContent: 'message-content-validate-selected'
		};
		await instance.$ms.msgBox.showWarning(waningText, null, options);
		return false;
	}
	/**
	 * Clone data (object, array)
	 * @param {*} data  dữ liệu
	 * @param {*} notRef  : Có tham chiếu hay không, True: dữ liệu clone không tham thiếu, false: có tham chiếu(thay đổi 2 chiều)
	 * @returns
	 */
	cloneData(data, notRef = true) {
		let dataClone = clone(data, notRef);
		return dataClone;
	}

	/**
	 * Clone các loại đối tượng k có tham chiếu
	 */
	cloneDeepData(data) {
		let dataDeepClone = cloneDeep(data);
		return dataDeepClone;
	}

	isObjectEmpty(object) {
		return !object || Object.keys(object).length === 0;
	}

	appendObjToArray(thatArray, newObj) {
		const frozenObj = Object.freeze(newObj);
		return Object.freeze(thatArray.concat(frozenObj));
	}

	redirectToStore(licenseInfo, callback) {
		if (!licenseInfo) return;
		//Tài nguyên của HCSN
		if (licenseInfo.IsGovernment) {
			messageBox.showWarning(
				`${i18n.t('i18nNotification.Notification').SupportLicenseHCSN(window.__env.authConfig.linkMisaContact)}`
			);
			return;
		}

		if (callback) {
			callback();
		}
	}
	async showReloadPage() {
		const me = this;
		let ans = await messageBox.showConfirm(`${i18n.t('i18nNotification.Notification.PleaseReloadPage')}`, {
			title: `${i18n.t('i18nNotification.TitleNotification')}`,
			btnConfig: [
				{
					key: 'Yes',
					target: 'Center',
					text: `${i18n.t('i18nNotification.Notification.Agree')}`,
					focus: true
				}
			]
		});
		if (ans) {
			window.location.reload();
		}
	}

	redirectToStoreV2(window, licenseInfoNotification, actionType) {
		//Không có thông tin
		if (!licenseInfoNotification || Object.keys(licenseInfoNotification).length <= 0) return;

		//Lấy link từ Ban CNTT trả ra để điều hướng
		let link = '';
		switch (actionType) {
			case MSEnum.OrderType.Extend:
				link = licenseInfoNotification.extendButtonLink;
				break;
			case MSEnum.OrderType.BuyMore:
				link = licenseInfoNotification.buyMoreButtonLink;
				break;
			case MSEnum.OrderType.Upgrade:
				link = licenseInfoNotification.upgradeButtonLink;
				break;
		}
		if (!link) return;
		window.open(link, '_blank');
	}

	getTotalPage(lstData) {
		let totalInfo = {
			TotalAmount: 0,
			TotalAmountWithoutVat: 0,
			TotalVatAmount: 0
		};
		if (!lstData || lstData.length <= 0) totalInfo;
		lstData.forEach(item => {
			//Chỉ tính toán với hóa đơn HĐ mới + HĐ thay thế + HĐ điều chỉnh + HĐ bị điều chỉnh + HĐ chưa xác định
			let isCalculator =
				item.StatusInvoice == MSEnum.TThaiHDonEnum.Xoa || item.StatusInvoice == MSEnum.TThaiHDonEnum.DaThayThe;
			if (!isCalculator) {
				let signVal = item.IsDiscountInvoice ? -1 : 1;
				totalInfo.TotalAmount += item.TotalAmountExchange * signVal;
				totalInfo.TotalAmountWithoutVat += item.TotalAmountWithoutVatExchange * signVal;
				totalInfo.TotalVatAmount += item.TotalVATAmountExchange * signVal;
			}
		});
		return totalInfo;
	}

	getTotalPageV2(lstData) {
		let totalInfo = {
			TotalAmount: 0,
			TotalAmountWithoutVat: 0,
			TotalVatAmount: 0
		};
		if (!lstData || lstData.length <= 0) totalInfo;

		lstData.forEach(item => {
			//Chỉ tính toán với hóa đơn HĐ mới + HĐ thay thế + HĐ điều chỉnh + HĐ bị điều chỉnh + HĐ chưa xác định
			let isCalculator =
				item.StatusInvoice == MSEnum.TThaiHDonEnum.Xoa || item.StatusInvoice == MSEnum.TThaiHDonEnum.DaThayThe;
			if (!isCalculator) {
				let signVal = item.IsDiscountInvoice ? -1 : 1;
				totalInfo.TotalAmount += item.TotalAmountExchange; //PBI 367925 Thay đổi giá trị trên giao diện rồi nên không nhân với signVal
				totalInfo.TotalAmountWithoutVat += item.TotalAmountWithoutVatExchange * signVal;
				totalInfo.TotalVatAmount += item.TotalVATAmountExchange * signVal;
			}
		});
		return totalInfo;
	}

	/**
	 * Lấy số bản ghi trên trang được người dùng chọn
	 */
	getTakePageSize() {
		const me = this;
		try {
			let takePageText = localStorage.getItem(KeyTakePage);
			if (takePageText) {
				let takePage = parseInt(takePageText);
				if (takePage != 0 && !isNaN(takePage)) {
					store.commit('updateTakeItem', takePage);
					return takePage;
				}
			}
		} catch (ex) {
			console.log(ex);
		}
		return 20;
	}

	/**
	 * Lưu lại số bản ghi trên trang được người dùng chọn và thông báo cho các grid khác
	 * @param {*} takePage
	 */
	setTakePageSize(takePage) {
		const me = this;
		try {
			localStorage.setItem('TakePage', takePage);
			store.commit('updateTakeItem', takePage);
		} catch (ex) {
			console.log(ex);
		}
	}

	/**
	 * Maping từ html element thành đối tượng vuejs để thao tác
	 * @param {object} dom html dome element
	 */
	mapDomToVue(dom) {
		return dom.__vue__;
	}

	/**
	 * Hàm cập nhật trạng thái đã kết nối với CQT
	 * @param {*} moduleName
	 * @param {*} res
	 * @returns
	 */
	updateDataConnectTaxaxion(moduleName, res) {
		let dataConnectTaxaxion = null;
		if (res && res.Data && res.Data.ListInvoices && res.Data.ListInvoices.length > 0) {
			dataConnectTaxaxion = res.Data.ListInvoices.find(
				x => x && x.ConnectType == MSEnum.ConnectInvoiceEnum.Taxaxion
			);
		}
		store.commit(`${moduleName}/updateDataConnectTaxaxion`, dataConnectTaxaxion);
		return dataConnectTaxaxion;
	}

	/**
	 * Lấy loại mẫu theo mẫu số và ký hiệu
	 * @param {*} templateNo  - mẫu số
	 * @param {*} series - ký hiệu
	 * @returns
	 */
	GetTemplate(templateNo, series) {
		const me = this;
		if (!templateNo && !series) return '';
		switch (templateNo) {
			case `${MSEnum.KyHieuMauSoEnum.GTGT}`:
				return MSEnum.InvoiceCustomTemplate.Invoice123;
			case `${MSEnum.KyHieuMauSoEnum.BanHang}`:
				return MSEnum.InvoiceCustomTemplate.InvoiceSales;
			case `${MSEnum.KyHieuMauSoEnum.TSanCong}`:
				return MSEnum.InvoiceCustomTemplate.Invoice123;
			case `${MSEnum.KyHieuMauSoEnum.DTruQuocGia}`:
				return MSEnum.InvoiceCustomTemplate.Invoice123;
			case `${MSEnum.KyHieuMauSoEnum.Khac}`:
				return MSEnum.InvoiceCustomTemplate.Invoice123;
			case `${MSEnum.KyHieuMauSoEnum.ChungTuNhuHDon}`:
				if (me.GetHDon(templateNo, series) === '06_01') {
					//Nội bộ
					return MSEnum.InvoiceCustomTemplate.InternalWarehouseExport;
				} else if (me.GetHDon(templateNo, series) === '06_02') {
					//Điều chuyển đại lý
					return MSEnum.InvoiceCustomTemplate.AgentWarehouseExport;
				} else {
					return MSEnum.InvoiceCustomTemplate.Invoice123;
				}
			default:
				return '';
		}
	}
	/**
	 * Lấy lại hóa đơn
	 * @param {*} templateNo
	 * @param {*} series
	 * @returns
	 */
	GetHDon(numberTemplateNo, series) {
		//Chỉ xử lý đối với hóa đơn 123
		if (numberTemplateNo <= 0) return '';

		//Nếu là hóa đơn điều chuyển nội bộ, xuất bán đại lý...
		if (numberTemplateNo == 6) {
			//Nếu là điều chuyển nội bộ - C22NAB
			let regex = /(([A-Z]{1})(\\d{2})N([A-Z0-9]{2}))/;
			let regexSecond = /(([A-Z]{1})(\\d{2})B([A-Z0-9]{2}))/;
			if (regex.test(series)) {
				return '06_01';
			}
			//Nếu là gửi bán đại lý - C22BAB
			else if (regexSecond.test(series)) {
				return '06_02';
			}
		}

		//Giá trị mặc định
		return numberTemplateNo || '';
	}
	/**
	 * Làm tròn số sau dấu phẩy
	 * @param {*} value
	 * @returns
	 * @author dqduy 27.02.2024
	 */
	roundTwoDigis(value) {
		return value ? Math.round(value * 100) / 100 : 0;
	}

	getMessageCheckedSeller(checkingResult, type, currentItem) {
		const me = this;
		if (!currentItem || !currentItem.SellerTaxCode) {
			return i18n.t(`i18nInvoice.Invoice.SellerUnchecked`);
		}
		if (
			checkingResult &&
			checkingResult.Address.Status == MSEnum.CheckingStatus.Valid &&
			checkingResult.Name.Status == MSEnum.CheckingStatus.Valid
		) {
			return i18n.t(`i18nInvoice.Invoice.${type}Valid`);
		} else if (
			checkingResult &&
			checkingResult.Name.Status == MSEnum.CheckingStatus.Unknown &&
			checkingResult.Address.Status == MSEnum.CheckingStatus.Unknown
		) {
			return i18n.t(`i18nInvoice.Invoice.${type}Unknown`);
		} else if (
			checkingResult &&
			(checkingResult.Name.Status == MSEnum.CheckingStatus.Unknown ||
				checkingResult.Address.Status == MSEnum.CheckingStatus.Unknown)
		) {
			return i18n.t(`i18nInvoice.Invoice.SellerMissSomeInfor`);
		} else if (
			checkingResult &&
			(checkingResult.Address.Status == MSEnum.CheckingStatus.Invalid ||
				checkingResult.Name.Status == MSEnum.CheckingStatus.Invalid)
		) {
			return i18n.t(`i18nInvoice.Invoice.${type}Invalid`);
		} else {
			return i18n.t(`i18nInvoice.Invoice.${type}Unchecked`);
		}
	}

	getMessageChecked(checkingResult, type, currentItem) {
		const me = this;
		let result = null;
		if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Valid && checkingResult.AcceptOther) {
			result = i18n.t(`i18nInvoice.Invoice.${type}ValidChecked`);
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Valid) {
			//ppdang 24.11.2021 đối với trường hợp thông báo phát hành sử dụng của công ty/tổng công ty
			if (type == 'Published' && currentItem && currentItem.IsUserPublishNoticeCompany) {
				result = i18n.t(`i18nInvoice.Invoice.PublishedValidPrimary`);
			} else if (
				currentItem &&
				currentItem.IsCheckReceipt &&
				(type == 'BuyerName' || type == 'BuyerAddress' || type == 'BuyerTaxCode') &&
				!checkingResult.OnInvoice &&
				!checkingResult.IsAccepted
			) {
				result = i18n.t(`i18nInvoice.Invoice.${type}Unknown`);
			} else {
				result = i18n.t(`i18nInvoice.Invoice.${type}Valid`);
			}
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Unknown) {
			//PPDANG: Xử lý cho trường hợp không đủ thông tin CKS
			if (type == 'SellerSignatureStatus') {
				if (checkingResult.HasSigned.Status == MSEnum.CheckingStatus.Unknown) {
					result = i18n.t(`i18nInvoice.Invoice.${type}Unknown`);
				} else {
					if (checkingResult.DataWasModified.Status == MSEnum.CheckingStatus.Invalid) {
						result = i18n.t(`i18nInvoice.Invoice.${type}Invalid`);
					} else {
						result = i18n.t(`i18nInvoice.Invoice.SellerSignatureStatusMissSomeInfo`);
					}
				}
			} else if (type == 'Buyer') {
				//Thông tin người mua
				if (
					checkingResult.TaxCode.Status == MSEnum.CheckingStatus.Unknown &&
					checkingResult.Name.Status == MSEnum.CheckingStatus.Unknown &&
					checkingResult.Address.Status == MSEnum.CheckingStatus.Unknown
				) {
					result = i18n.t(`i18nInvoice.Invoice.${type}Unknown`);
				} else {
					result = i18n.t(`i18nInvoice.Invoice.BuyerMissSomeInfo`);
				}
			} else {
				result = i18n.t(`i18nInvoice.Invoice.${type}Unknown`);
			}
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Invalid) {
			if (type == 'SellerActiveStatus' && !isNullOrEmpty(checkingResult.StatusInfoText)) {
				result = me.getStatusInfoText(checkingResult);
			} else {
				result = i18n.t(`i18nInvoice.Invoice.${type}Invalid`);
			}
		} else {
			result = i18n.t(`i18nInvoice.Invoice.${type}Unchecked`);
		}
		if (currentItem.IsCheckReceipt) {
			//15.08.2022 NNTHAO Không sử dụng được .replaceAll do WebView của SME không có hàm này
			do {
				result = result.replace(`${i18n.t('i18nCommon.invoice')}`, `${i18n.t('i18nCommon.receipt')}`);
				result = result.replace(`${i18n.t('i18nCommon.Invoice')}`, `${i18n.t('i18nCommon.Receipt')}`);
			} while (
				result.contains(`${i18n.t('i18nCommon.invoice')}`) ||
				result.contains(`${i18n.t('i18nCommon.Invoice')}`)
			);
		}
		return result;
	}

	//Trả text tiếng việt và text theo ngôn  ngữ
	getMessageCheckedV2(checkingResult, type, currentItem) {
		const me = this;
		let result = null;
		if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Valid && checkingResult.AcceptOther) {
			result = {
				text: i18n.t(`i18nInvoice.Invoice.${type}ValidChecked`),
				textVn: i18n.t(`i18nInvoice.Invoice.${type}ValidChecked`, 'vi')
			};
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Valid) {
			//ppdang 24.11.2021 đối với trường hợp thông báo phát hành sử dụng của công ty/tổng công ty
			if (type == 'Published' && currentItem && currentItem.IsUserPublishNoticeCompany) {
				result = {
					text: i18n.t(`i18nInvoice.Invoice.PublishedValidPrimary`),
					textVn: i18n.t(`i18nInvoice.Invoice.PublishedValidPrimary`, 'vi')
				};
			} else {
				result = {
					text: i18n.t(`i18nInvoice.Invoice.${type}Valid`),
					textVn: i18n.t(`i18nInvoice.Invoice.${type}Valid`, 'vi')
				};
			}
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Unknown) {
			//PPDANG: Xử lý cho trường hợp không đủ thông tin CKS
			if (type == 'SellerSignatureStatus') {
				if (checkingResult.HasSigned.Status == MSEnum.CheckingStatus.Unknown) {
					result = {
						text: i18n.t(`i18nInvoice.Invoice.${type}Unknown`),
						textVn: i18n.t(`i18nInvoice.Invoice.${type}Unknown`, 'vi')
					};
				} else {
					if (checkingResult.DataWasModified.Status == MSEnum.CheckingStatus.Invalid) {
						result = {
							text: i18n.t(`i18nInvoice.Invoice.${type}Invalid`),
							textVn: i18n.t(`i18nInvoice.Invoice.${type}Invalid`)
						};
					} else {
						result = {
							text: i18n.t(`i18nInvoice.Invoice.SellerSignatureStatusMissSomeInfo`),
							textVn: i18n.t(`i18nInvoice.Invoice.SellerSignatureStatusMissSomeInfo`, 'vi')
						};
					}
				}
			} else if (type == 'Buyer') {
				//Thông tin người mua
				if (
					checkingResult.TaxCode.Status == MSEnum.CheckingStatus.Unknown &&
					checkingResult.Name.Status == MSEnum.CheckingStatus.Unknown &&
					checkingResult.Address.Status == MSEnum.CheckingStatus.Unknown
				) {
					result = {
						text: i18n.t(`i18nInvoice.Invoice.${type}Unknown`),
						textVn: i18n.t(`i18nInvoice.Invoice.${type}Unknown`, 'vi')
					};
				} else {
					result = {
						text: i18n.t(`i18nInvoice.Invoice.BuyerMissSomeInfo`),
						textVn: i18n.t(`i18nInvoice.Invoice.BuyerMissSomeInfo`, 'vi')
					};
				}
			} else {
				result = {
					text: i18n.t(`i18nInvoice.Invoice.${type}Unknown`),
					textVn: i18n.t(`i18nInvoice.Invoice.${type}Unknown`, 'vi')
				};
			}
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Invalid) {
			if (type == 'SellerActiveStatus' && !isNullOrEmpty(checkingResult.StatusInfoText)) {
				result = me.getStatusInfoText(checkingResult);
			} else {
				result = {
					text: i18n.t(`i18nInvoice.Invoice.${type}Invalid`),
					textVn: i18n.t(`i18nInvoice.Invoice.${type}Invalid`, 'vi')
				};
			}
		} else {
			result = {
				text: i18n.t(`i18nInvoice.Invoice.${type}Unchecked`),
				textVn: i18n.t(`i18nInvoice.Invoice.${type}Unchecked`, 'vi')
			};
		}
		if (currentItem.IsCheckReceipt) {
			//15.08.2022 NNTHAO Không sử dụng được .replaceAll do WebView của SME không có hàm này
			do {
				result = {
					text: result.text.replace(`${i18n.t('i18nCommon.invoice')}`, `${i18n.t('i18nCommon.receipt')}`),
					textVn: result.textVn.replace(
						`${i18n.t('i18nCommon.invoice', 'vi')}`,
						`${i18n.t('i18nCommon.receipt', 'vi')}`
					)
				};
				result = {
					text: result.text.replace(`${i18n.t('i18nCommon.Invoice')}`, `${i18n.t('i18nCommon.Receipt')}`),
					textVn: result.textVn.replace(
						`${i18n.t('i18nCommon.Invoice', 'vi')}`,
						`${i18n.t('i18nCommon.Receipt', 'vi')}`
					)
				};
			} while (
				result.text.contains(`${i18n.t('i18nCommon.invoice')}`) ||
				result.text.contains(`${i18n.t('i18nCommon.Invoice')}`)
			);
		}
		return result;
	}

	getMessageCheckedAdvance(checkingResult, type, currentItem) {
		const me = this;
		let result = null;
		if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Valid && checkingResult.AcceptOther) {
			result = i18n.t(`i18nInvoice.Invoice.${type}ValidChecked`);
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Valid) {
			if (type == 'Published' && currentItem && currentItem.IsUserPublishNoticeCompany) {
				result = i18n.t(`i18nInvoice.Invoice.PublishedValidPrimary`);
			} else {
				result = i18n.t(`i18nInvoice.Invoice.${type}Valid`);
			}
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Invalid) {
			result = i18n.t(`i18nInvoice.Invoice.${type}Invalid`);
		} else {
			result = i18n.t(`i18nInvoice.Invoice.${type}Checking`);
		}
		if (currentItem.IsCheckReceipt) {
			//15.08.2022 NNTHAO Không sử dụng được .replaceAll do WebView của SME không có hàm này
			do {
				result = result.replace(`${i18n.t('i18nCommon.invoice')}`, `${i18n.t('i18nCommon.receipt')}`);
				result = result.replace(`${i18n.t('i18nCommon.Invoice')}`, `${i18n.t('i18nCommon.Receipt')}`);
			} while (
				result.contains(`${i18n.t('i18nCommon.invoice')}`) ||
				result.contains(`${i18n.t('i18nCommon.Invoice')}`)
			);
		}
		return result;
	}

	getIconCheckedSignature(signatureStatus) {
		const me = this;
		if (signatureStatus == MSEnum.CheckingStatus.Valid) {
			return `mi-icon-v2-checking-result-green-tick`;
		} else if (signatureStatus == MSEnum.CheckingStatus.Invalid) {
			return `mi-icon-v2-checking-result-red-tick`;
		} else if (signatureStatus == MSEnum.CheckingStatus.Unknown) {
			return `mi-icon-v2-checking-result-unknown-tick`;
		} else {
			return `mi-icon-v2-checking-result-load-orange`;
		}
	}

	getColorTextSignature(checkingResult) {
		const me = this;
		if (
			checkingResult &&
			(checkingResult.Status == MSEnum.CheckingStatus.Invalid ||
				checkingResult.Status == MSEnum.CheckingStatus.Unknown)
		) {
			return `color-red`;
		} else {
			return ``;
		}
	}

	/**
	 * Lấy text chữ ký số
	 */
	getMessageSignature(signatureStatus, sellerSignature, taxaxionSignature) {
		const me = this;
		let result = null;
		if (signatureStatus == MSEnum.CheckingStatus.Invalid) {
			result = i18n.t(`i18nInvoice.Invoice.SignatureStatusInvalid`);
		} else if (
			signatureStatus == MSEnum.CheckingStatus.Valid &&
			(!isNullOrEmpty(sellerSignature.AcceptOther) || !isNullOrEmpty(taxaxionSignature.AcceptOther))
		) {
			result = i18n.t(`i18nInvoice.Invoice.SignatureStatusValidChecked`);
		} else if (signatureStatus == MSEnum.CheckingStatus.Valid) {
			result = i18n.t(`i18nInvoice.Invoice.SignatureStatusValid`);
		} else if (signatureStatus == MSEnum.CheckingStatus.Unknown) {
			result = i18n.t(`i18nInvoice.Invoice.SignatureStatusUnknown`);
		} else {
			result = i18n.t(`i18nInvoice.Invoice.SignatureStatusUnchecked`);
		}
		return result;
	}

	getWaringAcceptByUser(checkingResult) {
		if (!isNullOrEmpty(checkingResult.AcceptOther)) {
			return 'warning-text-accept-other';
		}
		return null;
	}

	getIconChecked(checkingResult) {
		const me = this;
		if (
			checkingResult &&
			(checkingResult.Status == MSEnum.CheckingStatus.Valid) | !isNullOrEmpty(checkingResult.AcceptOther)
		) {
			return `mi-icon-v2-checking-result-green-tick`;
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Unknown) {
			//Thông tin CKS
			if (
				checkingResult &&
				checkingResult.DataWasModified &&
				checkingResult.DataWasModified.Status == MSEnum.CheckingStatus.Invalid
			) {
				return `mi-icon-v2-checking-result-red-tick`;
			}
			return `mi-icon-v2-checking-result-unknown-tick`;
		} else if (checkingResult && checkingResult.Status == MSEnum.CheckingStatus.Invalid) {
			return `mi-icon-v2-checking-result-red-tick`;
		} else {
			return `mi-icon-v2-checking-result-load-orange`;
		}
	}

	getIconCheckedAdvance(status) {
		const me = this;
		if (status == MSEnum.CheckingStatus.Valid) {
			return `mi-icon-v2-checking-result-green-tick`;
		} else if (status == MSEnum.CheckingStatus.Invalid) {
			return `mi-icon-v2-checking-result-red-tick`;
		}
		return `mi-icon-v2-checking-result-load-orange`;
	}

	getIconCheckedSeller(checkingResult, currentItem) {
		const me = this;
		if (!currentItem || !currentItem.SellerTaxCode) {
			return `mi-icon-v2-checking-result-red-tick`;
		}
		if (
			checkingResult &&
			checkingResult.Address.Status == MSEnum.CheckingStatus.Valid &&
			checkingResult.Name.Status == MSEnum.CheckingStatus.Valid &&
			(checkingResult.BusinessLocation.Status == MSEnum.CheckingStatus.Unchecked ||
				checkingResult.BusinessLocation.Status == MSEnum.CheckingStatus.Valid)
		) {
			return `mi-icon-v2-checking-result-green-tick`;
		} else if (
			checkingResult &&
			(checkingResult.Address.Status == MSEnum.CheckingStatus.Invalid ||
				checkingResult.Name.Status == MSEnum.CheckingStatus.Invalid ||
				checkingResult.BusinessLocation.Status == MSEnum.CheckingStatus.Invalid)
		) {
			return `mi-icon-v2-checking-result-red-tick`;
		} else if (
			checkingResult &&
			checkingResult.Address.Status == MSEnum.CheckingStatus.Unknown &&
			checkingResult.Name.Status == MSEnum.CheckingStatus.Unknown
		) {
			return `mi-icon-v2-checking-result-unknown-tick`;
		} else if (
			checkingResult &&
			(checkingResult.Address.Status == MSEnum.CheckingStatus.Unknown ||
				checkingResult.Name.Status == MSEnum.CheckingStatus.Unknown) &&
			checkingResult.Address.Status != MSEnum.CheckingStatus.ErrorOnChecking &&
			checkingResult.Name.Status != MSEnum.CheckingStatus.ErrorOnChecking
		) {
			return `mi-icon-v2-checking-result-unknown-tick`;
		} else if (
			checkingResult &&
			(checkingResult.Address.Status == MSEnum.CheckingStatus.Invalid ||
				checkingResult.Name.Status == MSEnum.CheckingStatus.Invalid)
		) {
			return `mi-icon-v2-checking-result-red-tick`;
		} else {
			return `mi-icon-v2-checking-result-load-orange`;
		}
	}

	getDataPaging(lstData, skip, take) {
		if (!lstData || lstData.length <= 0) {
			return {
				Data: [],
				TotalPage: 0,
				PageIndex: skip,
				PageSize: (take + 1) * skip
			};
		}
	}

	/**
	 * Hàm show pop mất kết nối với CQT
	 */
	async showFormDisconnectTaxaxionNotification(form, popupItems, params) {
		if (!window.__env.appSetting.isNeedShowDisconnectTax) return;
		if (!form) return;

		const me = this;
		let dataConnectTaxaxion = store.getters['connectInvoice/getDataConnectTaxaxion'];
		if (
			!dataConnectTaxaxion ||
			dataConnectTaxaxion.ConnectType != MSEnum.ConnectInvoiceEnum.Taxaxion ||
			dataConnectTaxaxion.Status != MSEnum.ConnectInvoiceStatus.Fail
		) {
			return;
		}

		//kiểm tra lại nếu chi nhánh mất kết nối mới show
		//Show thông báo mất kết nối
		let instance = await me.getPopupInstanceAsync(popupItems || {}, form);
		instance.currentItem = dataConnectTaxaxion;
		if (params && params.orgWorking) {
			instance.currentItem.OrgTaxCode = params.orgWorking.IdentityCode;
		}
		await instance.showPopup();
		if (params && params.callback && typeof params.callback == 'function') {
			params.callback();
		}
	}

	/**
	 * Lấy thông tin kết nối CQT của TH Mất kết nối
	 */
	getDataConnectTaxaxionFromDisconnect(params) {
		const me = this;
		let dataConnectTaxaxion = store.getters['connectInvoice/getDataConnectTaxaxion'];
		if (
			!dataConnectTaxaxion ||
			dataConnectTaxaxion.ConnectType != MSEnum.ConnectInvoiceEnum.Taxaxion ||
			dataConnectTaxaxion.Status != MSEnum.ConnectInvoiceStatus.Fail
		) {
			return null;
		}

		let currentItem = dataConnectTaxaxion;
		if (params && params.orgWorking) {
			currentItem.OrgTaxCode = params.orgWorking.IdentityCode;
		}

		return currentItem;
	}

	showFormWarningExpiredLicense(licenseInfo, instance, typeMessageLicense, titleConnect = '') {
		const me = this;
		if (!licenseInfo) return true;

		//Kiểm tra license
		let dateNow = new Date(); // Lấy ngày hiện tại
		let expiredDate = new Date(licenseInfo.ExpiredDate); // Ngày hêt hạn license
		if (!licenseInfo.NumberInvStillDb || me.formatDate(dateNow) > me.formatDate(expiredDate)) {
			let linkInstructionsForExtension = `${window.__env.authConfig.help_url}${
					window.__env.authConfig.linkHelpExtendLicense
				}`,
				linkInstructionsForAdditionalPurchases = `${window.__env.authConfig.help_url}${
					window.__env.authConfig.linkHelpBuyLicense
				}`;
			instance.$ms.msgBox.show({
				message: instance
					.$t('i18nConnectInvoice')
					[typeMessageLicense](linkInstructionsForExtension, linkInstructionsForAdditionalPurchases),
				btnConfig: [
					{
						key: 'Close',
						target: 'Center',
						text: `${instance.$t('i18nCommon.Close')}`,
						focus: true
					}
				],
				close: true,
				title: titleConnect
			});
			return true;
		}
		return false;
	}

	/**
	 *Đã import thư viện hay chưa
	 */
	checkImportMisaLicenseNotification(scriptTags, srcLibary) {
		try {
			// Lấy tất cả các thẻ script trong body
			if (!scriptTags) return false;
			//Chuyển về array
			scriptTags = [...scriptTags];
			return scriptTags && scriptTags.find(script => script.src && script.src === srcLibary);
		} catch (ex) {
			console.log(ex);
		}
		return false;
	}

	/**
	 * Hàm mua ngay link sang misa store
	 */
	buyNow(window, licenseInfo, licenseInfoNotification) {
		const me = this;
		try {
			var param = {
				TaxCode: localStorage.getItem('SubscriberCode'),
				Type: MSEnum.OrderType.Upgrade
			};
			me.redirectToStore(licenseInfo, async () => {
				me.redirectToStoreV2(window, licenseInfoNotification, param.Type);
			});
		} catch (ex) {
			console.log(ex);
		}
	}

	/**
	 * Lấy dữ liệu từ các tham số mà Inbot cấu hình trên AVA
	 * Dữ liệu được xử lý từ tham số AVA trả về
	 */
	getDataFromParamsAVA(data, paramType) {
		const me = this;
		if (!data || !data.message || !data.message.params) return {};
		let obj = {};
		let dataParam = data.message.params.find(x => x.name == paramType);
		if (!dataParam || Object.keys(dataParam).length <= 0) return {};

		switch (paramType) {
			case 'timestamp':
				obj = me.getDataFromPramTimestamp(dataParam);
				break;
			case 'tax_code':
				obj = me.getDataFromPramTaxCode(dataParam);
				break;
			case 'customer&supplier':
				obj = me.getDataFromPramSupplier(dataParam);
				break;
			case 'active_status':
				obj = me.getDataFromPramActiveStatus(dataParam);
				break;
			default:
				break;
		}

		return obj;
	}

	/**
	 * trạng thái hoạt động
	 * @author dqduy 07.06.2024
	 * @param {*} data
	 * @returns
	 */
	getDataFromPramActiveStatus(data) {
		const me = this;
		if (!data) return {};
		return {
			activeStatus: data.value
		};
	}

	getDataFromPramTimestamp(data) {
		const me = this;
		if (!data) return {};

		//AVA không tra về dữ liệu thời gian
		if (!data.value) {
			//Lấy thông tin ngày trong quý này(mặc định là dữ liệu của quý này)
			return DateRange.caculateRangeFromPeriod(MSEnum.Period.ThisQuarter);
		}

		// Biểu thức chính quy để tìm kiếm ngày trong dạng dd/mm/yyyy
		const regex = /(\d{2}\/\d{2}\/\d{4})-(\d{2}\/\d{2}\/\d{4})/;
		// Sử dụng phương thức match của chuỗi để trích xuất các ngày
		const matches = data.value.match(regex);
		if (!matches || matches.length !== 3) {
			//console.log('Không tìm thấy ngày trong chuỗi.');
			return {};
		}

		// Hàm chuyển đổi chuỗi ngày tháng thành đối tượng Date
		return {
			fromDate: me.convertStringToDate(matches[1]),
			toDate: me.convertStringToDate(matches[2])
		};
	}

	getDataFromPramTaxCode(data) {
		const me = this;
		if (!data) return {};
		return {
			taxCode: data.value
		};
	}

	getDataFromPramSupplier(data) {
		const me = this;
		if (!data) return {};
		return {
			supplierName: data.value
		};
	}

	getComponentInfoByDataAVA(window, data) {
		const me = this;
		if (!data || !data.message || !data.message.name) return {};

		if (
			data.message.name == window.__env.avaConfig.total_invoice_number_report_risk ||
			data.message.name == window.__env.avaConfig.total_invoice_number_report_risk_by_org ||
			data.message.name == window.__env.avaConfig.total_amount_report_invoice_risk ||
			data.message.name == window.__env.avaConfig.total_amount_report_invoice_risk_by_org
		) {
			return { name: 'ReportInvoiceRiskBySupplierList', path: '/report/report-invoice-risk-by-supplier' };
		} else if (
			data.message.name == window.__env.avaConfig.get_active_status_supplier ||
			data.message.name == window.__env.avaConfig.get_supplier_by_status
		) {
			return { name: 'SupplierList', path: '/supplier' };
		} else if (
			data.message.name == window.__env.avaConfig.total_report_invoices_by_active_suppliers ||
			data.message.name == window.__env.avaConfig.total_report_invoices_by_inactive_suppliers ||
			data.message.name == window.__env.avaConfig.total_report_invoices_by_notsure_suppliers ||
			data.message.name == window.__env.avaConfig.total_report_invoices_by_org ||
			data.message.name == window.__env.avaConfig.total_amount_report_invoices_by_active_suppliers ||
			data.message.name == window.__env.avaConfig.total_amount_report_invoices_by_inactive_suppliers ||
			data.message.name == window.__env.avaConfig.total_amount_report_invoices_by_notsure_suppliers ||
			data.message.name == window.__env.avaConfig.total_amount_report_invoices_by_org
		) {
			return { name: 'ReportInvoiceSupplierActiveList', path: '/report/report-invoice-by-supplier-active' };
		}

		return {};
	}

	isInvoiceReduction(taxReductions, invoiceDate) {
		const me = this;
		return (
			taxReductions &&
			invoiceDate &&
			taxReductions
				.filter(x => x.IsEnable)
				.find(
					x =>
						me.formatDate(invoiceDate, 'YYYY/MM/DD') >= x.StartDate &&
						me.formatDate(invoiceDate, 'YYYY/MM/DD') <= x.EndDate
				)
		);
	}

	checkRangeReduction(taxReductions, startDate, endDate) {
		const me = this;
		return (
			taxReductions &&
			startDate &&
			endDate &&
			taxReductions
				.filter(x => x.IsEnable)
				.find(
					x =>
						me.formatDate(startDate, 'YYYY/MM/DD') >= x.StartDate &&
						me.formatDate(endDate, 'YYYY/MM/DD') <= x.EndDate
				)
		);
	}

	/**
	 * Loại bỏ các thông tin cached trong Store khi có phiên bản mới
	 * @param {*} version
	 */
	resetStateConfig(version) {
		store.commit('resetStateConfig', version);
	}

	duplicateRangeDate(data1, data2) {
		const me = this;
		if (!data1 || !data2) return false;
		let start1 = me.formatDate(data1.StartDate, 'YYYY/MM/DD');
		let end1 = me.formatDate(data1.EndDate, 'YYYY/MM/DD');
		let start2 = me.formatDate(data2.StartDate, 'YYYY/MM/DD');
		let end2 = me.formatDate(data2.EndDate, 'YYYY/MM/DD');
		return (
			(start2 <= end1 && start2 >= start1) ||
			(end2 <= end1 && end2 >= start1) ||
			(start2 <= start1 && end2 >= end1)
		);
	}

	/**
	 * Check có được thực hiện hành động cập nhật chi phí cho hóa đơn
	 */
	checkShowButtonUpdateCost(data, settingSystem, isInvRevEmail) {
		const me = this;
		//Không thiết lập tự động phân loại hóa đơn => k được cập nhật định mức
		if (!settingSystem || !settingSystem.IsCostNorms || !isInvRevEmail) {
			return false;
		}

		//HĐ không thỏa mãn điều kiện được cập nhật định mức chi phí
		if (
			data.IsDeleted ||
			data.CostType == MSEnum.CostTypeEnum.None ||
			data.StatusInvoice == MSEnum.TThaiHDonEnum.DaThayThe ||
			data.StatusInvoice == MSEnum.TThaiHDonEnum.Xoa ||
			data.IsDiscountInvoice
		) {
			return false;
		}
		return true;
	}

	convertInvoices(invoices = [], columns = []) {
		const me = this;
		if (!invoices || invoices.length <= 0) return [];
		return invoices.forEach(item => {
			if (item.IsDiscountInvoice) {
				columns.forEach(col => {
					if (item[col] && item[col] > 0) item[col] = -1 * item[col];
				});
			}
		});
	}

	/**
	 * Lọc ra những cơ cấu tổ chức user có quyền truy cập
	 * @param {*} organizationUnits - Danh sách cơ cấu tổ chức
	 * @param {*} user - Thông tin user
	 * @returns
	 */
	getOrganizationUnitsPermissionByUserId(organizationUnits, user) {
		if (!organizationUnits || organizationUnits.length <= 0 || !user) return [];

		//Nếu là Admin thì có full quyền vào cctc
		if (user.Role == MSEnum.Role.Admin) {
			return organizationUnits;
		}

		//Nếu không có quyền truy cập vào cctc nào thì trả về rỗng
		if (!user.AccessOnOrgs || user.AccessOnOrgs.length <= 0) return [];

		//Lọc ra các cctc mà user có quyền truy cập
		return organizationUnits.filter(x => {
			return user.AccessOnOrgs.some(i => i == x.Id);
		});
	}

	getLinkRelativeInvoice() {
		const me = this;
		return `
				<span class="text"><span class="number">1</span> <a class="link" href="${
					window.__env.authConfig.linkUploadInvoice
				}" target="_blank">${i18n.t('i18nInvoice.InvoiceDetail.UploadInvoice')}</a></span>
				<span class="text"><span class="number">2</span> <a class="link" href="${
					window.__env.authConfig.linkSendEmailVia
				}" target="_blank">${i18n.t('i18nInvoice.InvoiceDetail.SendEmailInvoice')}</a></span>
				<span class="text"><span class="number">3</span> <a class="link" href="${
					window.__env.authConfig.linkDownloadInvoiceFromTaxAuthority
				}" target="_blank">${i18n.t('i18nInvoice.InvoiceDetail.DownloadInvoiceFromTax')}</a></span>
				<span class="text"><span class="number">4</span> <a class="link" href="${
					window.__env.authConfig.linkDownloadInvoiceFromSupplier
				}" target="_blank">${i18n.t('i18nInvoice.InvoiceDetail.DownloadInvoiceFromSupplier')}</a></span>
			`;
	}

	/**
	 * Định nghĩa các key cached client
	 */
	defindKeyCached(formType, keyEncCrypto = '8080808080808080', ivEncCrypto = '8080808080808080') {
		const me = this;
		let keyCached = '';
		if (formType == MSEnum.FormType.ReportSupplierActive) {
			keyCached = 'ms-report-supplier-active';
		}
		return me.EncodeCryptoJS(keyCached, keyEncCrypto, ivEncCrypto).toString();
	}

	/**
	 * Hàm dùng chung cho set dữ liệu client ở trình duyệt
	 */
	setItemClient(key, data, keyEncCrypto = '8080808080808080', ivEncCrypto = '8080808080808080') {
		const me = this;
		if (!key || !data) return;

		key = me.EncodeCryptoJS(key, keyEncCrypto, ivEncCrypto).toString();
		localStorage.setItem(key, JSON.stringify(data));
	}

	/**
	 * Hàm dùng chung cho get dữ liệu client ở trình duyệt
	 */
	getItemClient(key, keyEncCrypto = '8080808080808080', ivEncCrypto = '8080808080808080') {
		const me = this;
		if (!key) return;

		key = me.EncodeCryptoJS(key, keyEncCrypto, ivEncCrypto).toString();
		let dataClient = localStorage.getItem(key);
		if (!dataClient) return null;
		return JSON.parse(dataClient);
	}

	/**
	 * Hàm kiêm tra ngày hiện tại đã vượt qua ngày truyền vào mấy ngày
	 * @param {*} date
	 * @param {*} numberDay
	 * @returns
	 */
	checkCurrentPassedNumberDay() {
		const me = this;
		let featureTooltip = window.__env.newVersionFeatureTooltip;
		if (
			!featureTooltip ||
			!featureTooltip.DateRelease ||
			!featureTooltip.DayNumberShow ||
			featureTooltip.DayNumberShow <= 0
		)
			return false;

		const startDate = moment(featureTooltip.DateRelease, 'DD/MM/YYYY').startOf('day');
		const endDate = startDate
			.clone()
			.add(Number(featureTooltip.DayNumberShow) - 1, 'days')
			.endOf('day'); // 23h59'59'' ngày cuối cùng
		const today = moment();
		return today.isBetween(startDate, endDate, 'day', '[]');
	}

	getBrowserInfo() {
		let browserInfo = {
			navigationType: MSEnum.NavigationType.Unknown,
			userAgent: navigator.userAgent
		};
		if (browserInfo.userAgent.includes('Chrome') && !browserInfo.userAgent.includes('Edg')) {
			browserInfo.navigationType = MSEnum.NavigationType.Chorme;
			browserInfo.isChrome = true;
		} else if (browserInfo.userAgent.includes('Edg')) {
			browserInfo.navigationType = MSEnum.NavigationType.Edge;
			browserInfo.isEdge = true;
		} else if (browserInfo.userAgent.includes('Firefox')) {
			browserInfo.navigationType = MSEnum.NavigationType.Firefox;
			browserInfo.isFirefox = true;
		} else if (browserInfo.userAgent.includes('Safari') && !browserInfo.userAgent.includes('Chrome')) {
			browserInfo.navigationType = MSEnum.NavigationType.Safari;
			browserInfo.isSafari = true;
		} else if (browserInfo.userAgent.includes('Trident')) {
			browserInfo.navigationType = MSEnum.NavigationType.Trident;
			browserInfo.isTrident = true;
		}
		return browserInfo;
	}

	/**
	 * Check hiển thị thông báo gợi ý nhắc nhở trong phiên đăng nhập
	 * @author dqduy 18.10.2024
	 */
	checkShowNotificationHint() {
		const me = this;
		let keyCached = 'ms-notification-hint';
		let dataCached = localStorage.getItem(keyCached);
		if (dataCached) return false;
		return true;
	}

	checkShowNotificationForUser(featureTooltips) {
		//Check xem thời điểm hiện tại có thông báo giới thiệu nào của người dùng được show hay không
		//Check nếu có thì mới khởi tạo component tooltip
		const me = this;
		let userConfig = store.getters['user/UserConfig'];
		const currentVersion = me.getCurrentRoundVersion();
		if (
			!userConfig ||
			userConfig.VersionFeatureTooltip != currentVersion ||
			!userConfig.FeatureTooltips ||
			userConfig.FeatureTooltips.length <= 0 ||
			!featureTooltips ||
			featureTooltips.length <= 0
		)
			return;

		let tooltips = [];
		featureTooltips.forEach(element => {
			const tooltip = userConfig.FeatureTooltips.find(item => item.ConfigId == element.Id && !item.IsDismissed);
			if (tooltip) {
				tooltips.push(element);
			}
		});

		if (tooltips) {
			//Nếu có thì thực hiện bắn event
			EventBusGlobal.$emit(GlobalEventName.RedirectToSidebar, tooltips);
		}
	}

	/**
	 * Lấy ra thông tin tooltip theo id thỏa mãn điều kiện
	 * 1. Có thông tin trong bảng cấu hình tooltip
	 * 2. User có thông tin đó và chưa dc tắt đi
	 * => Nếu không thỏa mãn thì return null
	 */
	getFeatureTooltipById(idTooltip) {
		const me = this;
		let userConfig = store.getters['user/UserConfig'];
		let featureTooltipConfig = store.getters['user/FeatureTooltipConfig'];
		const currentVersion = me.getCurrentRoundVersion();

		if (
			!userConfig ||
			userConfig.VersionFeatureTooltip != currentVersion ||
			!featureTooltipConfig ||
			featureTooltipConfig.length <= 0
		)
			return null;

		const itemConfig = featureTooltipConfig.find(x => x.Id == idTooltip);
		if (!itemConfig) return null;

		//Lấy lại danh sach tooltip nếu chung 1 router để gom nhóm
		const lstTooltipConfig = featureTooltipConfig.filter(x => x.Router == itemConfig.Router);
		const userTooltip = userConfig.FeatureTooltips.find(item => item.ConfigId == idTooltip && !item.IsDismissed);
		//Nếu không tìm thấy thông tin tooltip ở bảng config và trong user không có thông tin hoặc đã được tắt đi rồi thì thôi
		if (!itemConfig && !itemConfig) return null;

		return itemConfig;
	}

	getSrcImageFromYoutube(videoId, type) {
		let imageSrcType = '';
		switch (type) {
			case MSEnum.TypeImageYoutube.MaxResDefault:
				imageSrcType = 'maxresdefault';
				break;
			case MSEnum.TypeImageYoutube.SdDefault:
				imageSrcType = 'sddefault';
				break;
			case MSEnum.TypeImageYoutube.HqDefault:
				imageSrcType = 'hqdefault';
				break;
			case MSEnum.TypeImageYoutube.MqDefault:
				imageSrcType = 'mqdefault';
				break;
			case MSEnum.TypeImageYoutube.Default:
				imageSrcType = 'default';
				break;
			default:
				imageSrcType = 'maxresdefault';
				break;
		}
		return `https://img.youtube.com/vi/${videoId}/${imageSrcType}.jpg`;
	}

	//Loại bỏ các link css
	removeLinkCSS(linkCss) {
		const links = document.querySelectorAll('link[rel="stylesheet"]');
		if (links) {
			links.forEach(link => {
				if (link.href == linkCss) {
					link.parentNode.removeChild(link);
				}
			});
		}
	}

	//Loại bỏ các link js
	removeLinkJS(linkJs) {
		const scripts = document.querySelectorAll('script');
		if (scripts) {
			scripts.forEach(script => {
				if (script.src == linkJs) {
					script.parentNode.removeChild(script);
				}
			});
		}
	}

	/**
	 * Text hiển thị số tiền kiểm tra hình thức thanh toán
	 * @param {*} invoiceDate
	 * @param {*} verifyPaymentConfigs
	 * @returns
	 */
	getVerifyPamentMethod(invoiceDate, verifyPaymentConfigs) {
		const me = this;
		//Không có thông tin, trả ra mặc định
		if (!invoiceDate || !verifyPaymentConfigs) return '20';

		//Tìm kiếm điều kiện trong khoảng ngày của hóa đơn
		var verifyPaymentConfig = verifyPaymentConfigs.find(x => invoiceDate >= x.FromDate && invoiceDate <= x.ToDate);
		if (verifyPaymentConfig) return verifyPaymentConfig.PaymentAmountText;

		//Không có thông tin, trả ra mặc định
		return '20';
	}

	/**
	 * Kiểm tra xem ngày tạo (createDate) có lớn hơn 30 ngày so với ngày hiện tại hay không.
	 *
	 * @param {string | Date} createDate - Ngày cần kiểm tra, có thể ở dạng chuỗi hoặc đối tượng Date.
	 * @returns {boolean} - Trả về true nếu createDate lớn hơn 30 ngày so với ngày hiện tại, ngược lại là false.
	 */
	isDateOlderThanDays(createDate, days = 30) {
		//Không có giá trị
		if (!createDate) return false;

		// moment(createDate): Chuyển đổi createDate thành một đối tượng Moment.
		// moment().subtract(30, 'ss'): Lấy thời điểm cách đây 30 ngày tính từ hiện tại.
		// isBefore(...): Kiểm tra xem createDate có nằm trước thời điểm cách đây 30 ngày không.
		return moment(createDate).isBefore(moment().subtract(days, 'days'));
	}

	isTodayInRange(startDate, endDate) {
		//Không có giá trị
		if (!startDate || !endDate) return false;
		const today = moment().startOf('day'); // Đầu ngày hôm nay
		const start = moment(startDate, 'DD/MM/YYYY');
		const end = moment(endDate, 'DD/MM/YYYY').endOf('day'); // Cuối ngày kết thúc
		return today.isBetween(start, end, null, '[]'); // Bao gồm cả ngày bắt đầu và ngày kết thúc
	}

	isShowTagNew(idTag, value) {
		if (!idTag || !value) return false;

		let msTagNews =
			window.__env.newVersionFeatureTooltip && window.__env.newVersionFeatureTooltip.msTagNews
				? window.__env.newVersionFeatureTooltip.msTagNews
				: null;
		if (!msTagNews) return false;

		let tagNews = msTagNews.filter(x => x.id === idTag);
		if (!tagNews || tagNews.length <= 0) return false;

		return tagNews.some(item => item.enable && item.value === value);
	}

	skipTakeData(array, skip = 0, take = 20) {
		if (!array || array.length <= 0) return [];
		return array.slice(skip, skip + take);
	}

	/**
	 * Hàm check show tooltip khi nhấn trả nghiệm ngay
	 */
	handleCheckShowFeatureTooltip(router) {
		const me = this;
		if (!router) return;

		//Lấy thông tin tooltip của vòng hiện tại
		const featureTooltips = me.getFeatureAllTooltipByRouter(router);
		if (!featureTooltips) return;

		me.checkShowNotificationForUser(featureTooltips);
	}

	getFeatureAllTooltipByRouter(router) {
		const me = this;
		const featureTooltips = store.getters['user/FeatureTooltipConfig'];
		const currentVersion = me.getCurrentRoundVersion();
		return featureTooltips.filter(item => item.Version === currentVersion && item.Router.includes(router));
	}

	compareCreatedDate(date) {
		const me = this;
		return me.getDateDiff(date, new Date(), 'days') > window.__env.appSetting.numberDayShowReleaseAva;
	}

	getDateDiff(date1, date2, unit = 'days') {
		if (!date1 || !date2) return 0;
		let m1 = moment(new Date(date1));
		let m2 = moment(new Date(date2));
		return Math.abs(m2.diff(m1, unit) + 1);
	}

	isAfterDate(date1, date2) {
		if (!date1 || !date2) return 0;
		let m1 = moment(new Date(date1));
		let m2 = moment(new Date(date2));
		return m1.isAfter(m2);
	}

	isBeforeDate(date1, date2) {
		if (!date1 || !date2) return 0;
		let m1 = moment(new Date(date1));
		let m2 = moment(new Date(date2));
		return m1.isBefore(m2);
	}

	/**
	 * So sánh ngày lớn hơn hoặc bằng
	 * @param {*} date1
	 * @param {*} date2
	 * @returns
	 */
	greaterEquals(date1, date2) {
		if (!date1 || !date2) return 0;
		return moment(date1).isSameOrAfter(date2, 'day');
	}

	/**
	 * So sánh ngày lớn hơn
	 * @param {*} date1
	 * @param {*} date2
	 * @returns
	 */
	greaterThan(date1, date2) {
		if (!date1 || !date2) return 0;
		return moment(date1).isAfter(date2, 'day');
	}

	/**
	 * So sánh ngày nhỏ hơn hoặc bằng
	 * @param {*} date1
	 * @param {*} date2
	 * @returns
	 */
	lessEquals(date1, date2) {
		if (!date1 || !date2) return 0;
		return moment(date1).isSameOrBefore(date2, 'day');
	}

	/**
	 * So sánh ngày nhỏ hơn
	 * @param {*} date1
	 * @param {*} date2
	 * @returns
	 */
	lessThan(date1, date2) {
		if (!date1 || !date2) return 0;
		return moment(date1).isBefore(date2, 'day');
	}

	/**
	 * Hàm lấy ra mã số thuế ở phòng ban trong cctc
	 * @param {*} orgInfo
	 * @param {*} orgs : danh sách CCTC ở dạng dữ liệu trải phẳng
	 * @returns
	 */
	getTaxCodeForDepartment(orgInfo, orgs) {
		const me = this;
		if (!orgInfo || !orgInfo.Id || !orgs || orgs.length <= 0) return '';

		//Nếu là cty và chi nhánh thì trả về luôn mst
		if (
			(orgInfo.Type == MSEnum.TypeOrganization.Company && orgInfo.InvRevEmail) ||
			orgInfo.Type == MSEnum.TypeOrganization.Branch
		) {
			return orgInfo.TaxCode;
		}

		// Nếu là phòng ban
		let orgInfoResult = orgs.find(i => i.Id === orgInfo.Id);
		if (!orgInfoResult) return '';

		// Lấy ra chi nhánh theo của phòng ban
		orgInfoResult = orgs.find(i => i.Id === orgInfoResult.ParentId);
		return orgInfoResult ? orgInfoResult.TaxCode : '';
	}

	dateDiff(startDate, endDate, unit = 'days') {
		if (!startDate || !endDate) return null;
		const startD = moment(startDate, 'YYYY-MM-DD');
		const endD = moment(endDate, 'YYYY-MM-DD');
		const differenceInDays = endD.diff(startD, unit);
		return differenceInDays;
	}

	replaceTextInvoiceForReceipt(text, isReceipt) {
		if (!text || !isReceipt) return text;
		text = this.replaceAllExt(text, `${i18n.t('i18nCommon.invoice')}`, `${i18n.t('i18nCommon.receipt')}`);
		text = this.replaceAllExt(text, `${i18n.t('i18nCommon.Invoice')}`, `${i18n.t('i18nCommon.Receipt')}`);
		return text;
	}

	/**
	 * Hàm check license là doanh nghiệp hay HKD
	 * @param {*} customerType
	 * @param {*} CustomerTypeHKD - Cờ config xem customertype cho HKD bên license trả về là gì
	 * @returns
	 */
	GetSubscriberType(customerType, CustomerTypeHKD) {
		if (customerType && customerType === CustomerTypeHKD) {
			return MSEnum.SubscriberType.BusinessHousehold;
		}

		return MSEnum.SubscriberType.Enterprise;
	}

	/**
	 * Kiểm tra License có phải của Hộ kinh doanh hay không
	 * @param {Object} licenseInfo - Thông tin License
	 * @returns {Boolean} - Trả về true nếu là Hộ kinh doanh, ngược lại là false
	 */
	/**
	 * Kiểm tra License có phải của Hộ kinh doanh hay không
	 * @param {Object} licenseInfo - Thông tin License
	 * @returns {Boolean} - Trả về true nếu là Hộ kinh doanh, ngược lại là false
	 */
	isBusinessHousehold(licenseInfo) {
		// Kiểm tra CustomerType có phải là "KHD" không
		return licenseInfo && licenseInfo.CustomerType && licenseInfo.CustomerType === MSEnum.CustomerType.HKD;
	}

	/**
	 * Kiểm tra xem thời gian hiện tại có nhỏ hơn thời gian trong config hay không
	 * @param {String|Date} configTime Thời gian cấu hình cần so sánh
	 * @returns {Boolean} True nếu thời gian hiện tại nhỏ hơn hoặc bằng thời gian trong config, ngược lại là false
	 * @author PDDANG - 30.06.2025
	 */
	checkTimeLessThanConfigTime(configTime) {
		if (!configTime) return false;

		// Sử dụng moment để lấy thời gian hiện tại (bắt đầu của ngày)
		const currentDate = moment().startOf('day');
		// Chuyển đổi configTime thành đối tượng moment (cuối ngày)
		const endDate = moment(configTime).endOf('day');
		// So sánh thời gian hiện tại với thời gian trong config
		return currentDate.isSameOrBefore(endDate);
	}

	/**
	 * Lấy giá trị của JwtAuth trong lcoalStorage
	 */
	getJWTToken() {
		return localStorage.getItem('JwtAuth');
	}

	/**
	 *
	 * @returns Lấy thời gian hết hạn
	 */
	getExpiredAt() {
		return localStorage.getItem('ExpiredAt');
	}

	/**
	 * Xử lý những ký tự có vấn đề
	 * @param {*} base64Url
	 * @returns
	 */
	base64UrlToBase64(base64Url) {
		let b64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
		while (b64.length % 4) b64 += '=';
		return b64;
	}

	/**
	 * Kiểm tra token JWT còn hạn hay không
	 * @returns {boolean} true nếu token còn hạn, false nếu token hết hạn hoặc không hợp lệ
	 * @author ppdang
	 */
	isValidJWTToken(token) {
		const me = this;
		try {
			// Không tồn tại token
			if (!token) {
				// console.log('Lỗi hàm isValidJWTToken::Không có token.');
				return false;
			}

			// JWT token có cấu trúc: header.payload.signature
			const parts = token.split('.');
			if (parts.length !== 3) {
				// console.log('Lỗi hàm isValidJWTToken::Token không hợp lệ.');
				return false;
			}

			// Giải mã phần payload
			var atobText = me.base64UrlToBase64(parts[1]);
			const payload = JSON.parse(atob(atobText));

			// Kiểm tra expiration time
			const exp = payload.exp;
			if (!exp) {
				// console.log('Lỗi hàm isValidJWTToken::Không có Exp.');
				return false;
			}

			// Kiểm tra xem token đã hết hạn chưa
			// exp là thời gian hết hạn (dạng Unix timestamp - số giây từ epoch)
			const currentTime = Math.floor(Date.now() / 1000); // Chuyển sang Unix timestamp (giây)
			var result = exp > currentTime;
			if (!result) {
				// console.log('Lỗi hàm isValidJWTToken::Token hết hạn sử dụng.');
			}
			return result;
		} catch (error) {
			console.error('Error when validating JWT token:', error);
		}
		return false;
	}

	/**
	 * Các hóa đơn được check kèm với các thiết lập ẩn hiện của mẫu
	 */
	checkTemplateForSettingSystem(currentItem) {
		const me = this;
		return (
			(currentItem && currentItem.IsCommercialInvoiceHanding) ||
			me.GetTemplate(currentItem.TemplateNo, currentItem.Series) == MSEnum.InvoiceCustomTemplate.Invoice123
		);
	}

	//Thực hiện xóa dữ liệu trong LocalStorage khi logout hoặc làm mới dữ liệu
	removeLocalStorage() {
		const me = this;
		//Xóa cache bên inbot
		localStorage.removeItem(
			window.__env.misaLicenseNotification.identificationUpdateConfig.cacheKeyInbot ||
				'keyIdCardUpdateSessionInbot'
		);

		//Xóa cache localStorage của bên ban
		localStorage.removeItem(
			window.__env.misaLicenseNotification.identificationUpdateConfig.cacheKeyHiddenPopupBanCNTT ||
				'misa_aln_customer_identifier'
		);

		//Hằng số cố định thay cho window.__env.appSetting.systemFail.keyStorage — khớp Main.vue::keyStorageIdBanner
		const prefixKey = 'inbot_sys_banner_closed_';
		me.removeKeysByPattern(prefixKey);
	}

	//Xóa cache key của logout
	removeKeysByPattern(prefixKey) {
		Object.keys(localStorage)
			.filter(key => key.startsWith(prefixKey))
			.forEach(key => localStorage.removeItem(key));
	}

	//Hàm này dùng cho trường hợp đang đăng nhập 1 tài khoản, nhưng kích hoạt tài khoản khác
	//thì sẽ đăng xuất tài khoản cũ và đăng nhập tài khoản mới
	async resetStateDefault() {
		const me = this;
		//Xóa thông tin trong LocalStorage
		me.removeLocalStorage();
		//Reset tất cả trạng thái về như ban đầu khởi tạo
		store.commit('resetStateDefault');
	}

	/**
	 * Hàm thông tin CCTC cha của CCTC đang làm việc
	 * @param {*} orgInfo
	 * @param {*} orgs : danh sách CCTC ở dạng dữ liệu trải phẳng
	 * @returns
	 */
	getOrgInfoParent(orgInfo, orgs) {
		const me = this;
		if (!orgInfo || !orgInfo.Id || !orgs || orgs.length <= 0) {
			return {};
		}

		//Nếu là cty và chi nhánh thì trả về luôn mst
		if (
			orgInfo.InvRevEmail &&
			(orgInfo.Type == MSEnum.TypeOrganization.Company || orgInfo.Type == MSEnum.TypeOrganization.Branch)
		) {
			return orgInfo;
		}

		return orgs.find(i => i.Id === orgInfo.ParentId);
	}

	/**
	 * Hàm xử lý mở file blob theo loại file và tên file
	 */
	openPrintResultBlob(res, payload, nameFile) {
		const me = this;
		if (payload && payload.Type == MSEnum.PrintType.Template) {
			if (payload.FileType == MSEnum.FileType.Image) {
				me.openByBlob(res, 'image/png', `${nameFile}.png`);
			} else {
				me.openByBlob(res, 'application/pdf', `${nameFile}.pdf`);
			}
		} else {
			me.openByBlob(res, 'application/pdf', `${nameFile}.pdf`);
		}
	}

	checkQuarter(fromDate, toDate) {
		if (!fromDate || !toDate) {
			return { isQuarter: false };
		}

		const startDate = moment(fromDate);
		const endDate = moment(toDate);

		const quarterStart = startDate.clone().startOf('quarter');
		const quarterEnd = startDate.clone().endOf('quarter');

		const isValidQuarter =
			startDate.isSame(quarterStart, 'day') &&
			endDate.isSame(quarterEnd, 'day') &&
			startDate.isSame(endDate, 'quarter');

		if (!isValidQuarter) {
			return { isQuarter: false };
		}

		// Tính quý (1, 2, 3, 4)
		const quarter = Math.floor(startDate.month() / 3) + 1;
		const year = startDate.year();

		return {
			isQuarter: true,
			quarterInfo: {
				quarter: quarter,
				year: year,
				quarterName: `Quý ${quarter}/${year}`,
				quarterCode: `Q${quarter}-${year}`,
				fromDate: fromDate,
				toDate: toDate
			}
		};
	}

	/**
	 * Trả về thông tin quý trước đó dựa vào fromDate và toDate
	 * @param {string|Date} fromDate - Ngày bắt đầu
	 * @param {string|Date} toDate - Ngày kết thúc
	 * @param {number} compareType - Loại so sánh: MSEnum.CompareType.Previous (0) hoặc MSEnum.CompareType.SameLastYear (1)
	 * @returns {Object} Thông tin về quý trước đó
	 */
	getPreviousQuarter(fromDate, toDate, compareType) {
		// Kiểm tra tham số đầu vào
		if (!fromDate || !toDate) {
			return { isQuarter: false };
		}

		// Lấy thông tin quý hiện tại
		const currentQuarterInfo = this.checkQuarter(fromDate, toDate);

		// Nếu không phải là quý hợp lệ, trả về false
		if (!currentQuarterInfo.isQuarter) {
			return { isQuarter: false };
		}

		// Lấy thông tin quý và năm hiện tại
		const currentQuarter = currentQuarterInfo.quarterInfo.quarter;
		const currentYear = currentQuarterInfo.quarterInfo.year;

		// Mặc định là Previous nếu không truyền compareType
		if (!compareType && compareType !== 0) {
			compareType = MSEnum.CompareType.Previous;
		}

		let prevQuarter, prevYear;
		if (compareType === MSEnum.CompareType.SameLastYear) {
			// Lấy cùng quý năm trước
			prevQuarter = currentQuarter;
			prevYear = currentYear - 1;
		} else {
			// Lấy quý trước liền kề (logic cũ)
			prevQuarter = currentQuarter - 1;
			prevYear = currentYear;

			// Nếu quý hiện tại là quý 1, quý trước đó sẽ là quý 4 của năm trước
			if (prevQuarter === 0) {
				prevQuarter = 4;
				prevYear = currentYear - 1;
			}
		}

		// Tạo ngày bắt đầu và kết thúc của quý trước đó
		const prevQuarterStartDate = moment()
			.year(prevYear)
			.quarter(prevQuarter)
			.startOf('quarter');
		const prevQuarterEndDate = moment()
			.year(prevYear)
			.quarter(prevQuarter)
			.endOf('quarter');

		// Trả về kết quả
		return {
			isQuarter: true,
			quarterInfo: {
				quarter: prevQuarter,
				year: prevYear,
				quarterName: `Quý ${prevQuarter}/${prevYear}`,
				quarterCode: `Q${prevQuarter}-${prevYear}`,
				fromDate: prevQuarterStartDate.startOf('day').toDate(),
				toDate: prevQuarterEndDate.endOf('day').toDate()
			}
		};
	}

	/**
	 * Hàm tả về tên file "hóa đơn đầu ra, đầu vào" với cctc thường và "hóa đơn" với cctc đặc thù bên thứ 3
	 * @param {*} fileName - Tên file: Sẽ ưu tiên file name truyền vào
	 * @param {*} payload - Params
	 * @param {*} typeFile - Loại tên file trả về
	 * @returns
	 */
	getNameFileDownload(fileName, payload, typeFile = 'zip') {
		if (typeFile === 'zip') {
			if (fileName) {
				return `${fileName}.zip`;
			} else if (!payload.IsInbot) {
				return 'hoa_don_dau_ra.zip';
			} else {
				return payload.IsCheckThirdParty ? 'hoa_don.zip' : 'hoa_don_dau_vao.zip';
			}
		}
	}

	showFormMsgBoxRemoveHistoryActiveStatus() {
		messageBoxV2.show({
			message: i18n.t('i18nInvoice.Invoice.NotificationRemoveHistoryActiveStatus'),
			title: i18n.t('i18nInvoice.Invoice.ViewHistoryStatus'),
			isIconClose: true,
			width: '500px',
			classContent: 'msgbox-remove-history-active-status',
			classMessageContent: 'ms-mgt-20px mb-0',
			btnConfig: [
				{
					key: 'Close',
					target: 'Right',
					text: i18n.t('i18nCommon.Close'),
					typeButton: 'primary',
					focus: false
				}
			]
		});
	}

	/**
	 * Kiểm tra hóa đơn có áp dụng logic bỏ lịch sử tình trạng hoạt động hay không (PBI #591475)
	 * Trả về true nếu:
	 * 2. invoiceDate >= CUTOFF_DATE_REMOVE_HISTORY_ACTIVE_STATUS (1/7/2025)
	 * @param {String|Date|moment} invoiceDate - Ngày hóa đơn
	 * @returns {Boolean} true nếu áp dụng logic mới, false nếu giữ logic cũ
	 */
	isRemoveHistoryActiveStatus(config, invoiceDate) {
		// Kiểm tra feature flag
		// Kiểm tra ngày hóa đơn có giá trị
		if (
			!config ||
			!config.IsEnableRemoveHistoryActiveStatus ||
			!config.CutoffDateRemoveHistoryActiveStatus ||
			!invoiceDate
		) {
			return false;
		}

		// So sánh ngày hóa đơn với ngày mốc (chỉ so sánh date, không so sánh time)
		let invDate = moment(invoiceDate);
		let cutoffDate = moment(config.CutoffDateRemoveHistoryActiveStatus, 'YYYY-MM-DD');
		return invDate.isSameOrAfter(cutoffDate, 'day');
	}
	/**
	 * Kiểm tra tên file có phải là file PDF hợp lệ hay không.
	 *
	 * Xử lý các trường hợp:
	 * - "report.pdf"          → true
	 * - "report.PDF"          → true (không phân biệt hoa/thường)
	 * - "my.report.final.pdf" → true (nhiều dấu chấm, miễn cuối cùng là .pdf)
	 * - "abc.pdf.txt"         → false (phần mở rộng cuối cùng là .txt)
	 * - ".pdf"                → false (không có tên file, chỉ có extension)
	 * - "pdf"                 → false (không có dấu chấm)
	 * - ""                    → false (chuỗi rỗng)
	 * - null / undefined      → false
	 * - "file.pdf "           → false (có khoảng trắng thừa - tùy chọn trim)
	 * - "file. pdf"           → false (có khoảng trắng trong extension)
	 * - "file.pdf.gz"         → false
	 * - ".hidden.pdf"         → true (file ẩn trên Unix với tên hợp lệ)
	 *
	 * @param {string} fileName - Tên file cần kiểm tra
	 * @param {object} options - Tùy chọn bổ sung
	 * @param {boolean} options.trimWhitespace - Tự động trim khoảng trắng (mặc định: false)
	 * @param {boolean} options.allowUpperCase - Cho phép .PDF, .Pdf,... (mặc định: true)
	 * @returns {boolean} - true nếu file là PDF hợp lệ
	 */
	isPdfFile(fileName, options = {}) {
		const { trimWhitespace = false, allowUpperCase = true } = options;

		// 1. Kiểm tra input hợp lệ
		if (!fileName || typeof fileName !== 'string') {
			return false;
		}

		// 2. Trim khoảng trắng nếu được bật
		const name = trimWhitespace ? fileName.trim() : fileName;

		// 3. Kiểm tra chuỗi rỗng
		if (name.length === 0) {
			return false;
		}

		// 4. Tìm vị trí dấu chấm cuối cùng
		const lastDotIndex = name.lastIndexOf('.');

		// 5. Không có dấu chấm hoặc dấu chấm ở đầu mà không có gì sau nó
		if (lastDotIndex === -1) {
			return false;
		}

		// 6. Tách phần tên file (trước dấu chấm cuối) và extension (sau dấu chấm cuối)
		const baseName = name.substring(0, lastDotIndex);
		const extension = name.substring(lastDotIndex + 1);

		// 7. Phần tên file không được rỗng
		//    → Loại trường hợp ".pdf" (chỉ có extension, không có tên)
		if (baseName.length === 0) {
			return false;
		}

		// 8. So sánh extension
		const ext = allowUpperCase ? extension.toLowerCase() : extension;

		return ext === 'pdf';
	}

	/**
	 * Thực hiện lấy id của header banner thông báo trong config
	 * @returns
	 */
	idBanner() {
		return window.__env &&
			window.__env.appSetting &&
			window.__env.appSetting.systemFail &&
			window.__env.appSetting.systemFail.id
			? window.__env.appSetting.systemFail.id
			: null;
	}

	/**
	 * Thực hiện lấy key storage của header banner
	 * @returns
	 */
	keyStorageIdBanner() {
		const me = this;
		const prefixKey =
			window.__env &&
			window.__env.appSetting &&
			window.__env.appSetting.systemFail &&
			window.__env.appSetting.systemFail.keyStorage
				? window.__env.appSetting.systemFail.keyStorage
				: 'inbot_sys_banner_closed_';
		return `${prefixKey}${me.idBanner()}`;
	}
}

export default new CommonFunction();
